import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { zohoApi, leadsApi, orderTrackingApi, booksApi } from "../lib/api";
import { getCourierTrackingLink } from "../lib/courierTracking";
import { sanitizeHtml } from "../lib/sanitize";
import { toast } from "sonner";
import Customer360Panel from "../components/Customer360Panel";
import EmailLinkedLeadPanel from "../components/email/EmailLinkedLeadPanel";
import { ProductLookupTab } from "../components/chat/ProductLookupTab";
import {
  Mail, Search, Loader2, ExternalLink, Send, RefreshCw, Inbox, User as UserIcon, X,
  Paperclip, History, CheckCircle, FileText, Clock, MessageSquare, Download,
  Star, UserPlus, Maximize2, Minimize2, Link as LinkIcon, ChevronDown, ChevronUp,
  Sparkles, Phone, Save, Trash2, Reply, SlidersHorizontal, Package, BookOpen,
  PanelRightOpen, PanelRightClose, Truck, Forward, Users as UsersIcon, CornerUpLeft, ChevronLeft,
  Lock, IdCard, Wand2,
} from "lucide-react";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Open", label: "Open" },
  { value: "On Hold", label: "On Hold" },
  { value: "Escalated", label: "Escalated" },
  { value: "Closed", label: "Closed" },
];

const QUICK_VIEWS = [
  { key: "mine", label: "My Tickets", icon: UserIcon },
  { key: "starred", label: "Starred", icon: Star },
  { key: "open", label: "All Open", icon: Inbox },
  { key: "overdue", label: "Overdue", icon: Clock },
];

const statusBadgeColor = (statusType, status) => {
  if (statusType === "Open" || status === "Open") return "bg-green-50 text-green-700 border-green-200";
  if (statusType === "Closed" || status === "Closed") return "bg-gray-100 text-gray-600 border-gray-200";
  if (status === "Escalated") return "bg-red-50 text-red-700 border-red-200";
  return "bg-amber-50 text-amber-700 border-amber-200";
};

// Compute "X hours ago" / "X days ago" from an ISO timestamp
const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const EmailsPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [zohoStatus, setZohoStatus] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(false); // full-page mode

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [status, setStatus] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [starredOnly, setStarredOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [quickView, setQuickView] = useState(null);
  const [savedViews, setSavedViews] = useState([]);
  const [zohoViews, setZohoViews] = useState([]);
  const [zohoViewId, setZohoViewId] = useState("");
  const [sortBy, setSortBy] = useState("-modifiedTime");
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [viewNameInput, setViewNameInput] = useState("");

  const LIMIT = 100;

  // Find my Zoho agent id by matching CRM user email to Zoho agent email
  const myAgentId = agents.find((a) => a.email && user?.email && a.email.toLowerCase() === user.email.toLowerCase())?.id;

  const fetchAgents = useCallback(async () => {
    try {
      const res = await zohoApi.listAgents();
      setAgents(res.data.agents || []);
    } catch { }
  }, []);

  const fetchZohoViews = useCallback(async () => {
    try {
      const res = await zohoApi.listViews();
      setZohoViews(res.data.views || []);
    } catch { }
  }, []);

  const fetchSavedViews = useCallback(async () => {
    try {
      const res = await zohoApi.listSavedViews();
      setSavedViews(res.data.views || []);
    } catch { }
  }, []);

  const handleSaveView = async () => {
    const name = viewNameInput.trim();
    if (!name) { toast.error("Name required"); return; }
    try {
      await zohoApi.saveView({
        name,
        department_id: departmentId,
        status,
        assignee_id: assigneeId,
        starred_only: starredOnly,
        search,
      });
      toast.success(`Saved "${name}"`);
      setShowSaveDialog(false); setViewNameInput("");
      fetchSavedViews();
    } catch { toast.error("Save failed"); }
  };

  const handleDeleteView = async (id) => {
    if (!window.confirm("Delete this saved view?")) return;
    try {
      await zohoApi.deleteView(id);
      toast.success("View deleted");
      fetchSavedViews();
    } catch { toast.error("Delete failed"); }
  };

  const applySavedView = (v) => {
    const f = v.filters || {};
    setDepartmentId(f.department_id || "");
    setStatus(f.status || "");
    setAssigneeId(f.assignee_id || "");
    setStarredOnly(!!f.starred_only);
    setSearch(f.search || "");
    setSearchInput(f.search || "");
    setPage(0);
    setQuickView(null);
  };

  const fetchTickets = useCallback(async () => {
    if (!zohoStatus?.authorized) return;
    setLoading(true);
    try {
      const res = await zohoApi.listTickets({
        departmentId: departmentId || undefined,
        status: status || undefined,
        assigneeId: assigneeId || undefined,
        viewId: zohoViewId || undefined,
        starredOnly: starredOnly || undefined,
        search: search || undefined,
        from: page * LIMIT,
        limit: LIMIT,
        sortBy,
      });
      setTickets(res.data.tickets || []);
      setCount(res.data.count || 0);
      setHasMore(res.data.has_more || false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tickets");
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [departmentId, status, assigneeId, zohoViewId, starredOnly, search, page, sortBy, zohoStatus?.authorized]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await zohoApi.getStatus();
      setZohoStatus(res.data);
    } catch {
      setZohoStatus({ authorized: false });
    }
  }, []);

  useEffect(() => { fetchStatus(); fetchAgents(); fetchSavedViews(); fetchZohoViews(); }, [fetchStatus, fetchAgents, fetchSavedViews, fetchZohoViews]);
  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // URL param ?ticket=<id> — auto-select that ticket (deep link from ChatsPage / notifications)
  useEffect(() => {
    const ticketId = searchParams.get("ticket");
    if (!ticketId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await zohoApi.ticketDetails(ticketId);
        if (!cancelled && res.data) setSelected(res.data);
      } catch { /* ignore — ticket may be stale */ }
      finally {
        // Clean URL so refresh doesn't re-fetch
        if (!cancelled) {
          const next = new URLSearchParams(searchParams);
          next.delete("ticket");
          setSearchParams(next, { replace: true });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams]);

  const applyQuickView = (key) => {
    setQuickView(key);
    setPage(0);
    if (key === "mine") {
      setAssigneeId(myAgentId || "");
      setStatus("Open");
      setStarredOnly(false);
    } else if (key === "starred") {
      setStarredOnly(true);
      setAssigneeId("");
      setStatus("");
    } else if (key === "open") {
      setStatus("Open");
      setAssigneeId("");
      setStarredOnly(false);
    } else if (key === "overdue") {
      setStatus("Open");
      setAssigneeId("");
      setStarredOnly(false);
      // UI filter for overdue applied after fetch
    } else {
      setStarredOnly(false);
      setAssigneeId("");
      setStatus("");
    }
  };

  const handleSearch = (e) => { e.preventDefault(); setPage(0); setSearch(searchInput.trim()); };
  const clearSearch = () => { setSearchInput(""); setSearch(""); setPage(0); };

  const departmentName = (id) => zohoStatus?.departments?.find((x) => x.id === id)?.name || "—";
  const agentName = (id) => agents.find((a) => a.id === id)?.name || "Unassigned";

  const handleStarToggle = async (t) => {
    try {
      if (t.starred) await zohoApi.unstarTicket(t.id); else await zohoApi.starTicket(t.id);
      setTickets(prev => prev.map(x => x.id === t.id ? { ...x, starred: !x.starred } : x));
      if (selected?.id === t.id) setSelected({ ...selected, starred: !selected.starred });
    } catch (e) {
      toast.error("Star failed");
    }
  };

  // Filter to overdue on client side (Zoho doesn't have a direct overdue filter)
  const filteredTickets = quickView === "overdue" ? tickets.filter(t => t.isOverdue) : tickets;

  if (!zohoStatus) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[--accent]" /></div>;
  }

  if (!zohoStatus.authorized) {
    return (
      <div className="max-w-xl mx-auto mt-16 p-6 bg-amber-50 border border-amber-200 rounded-xl text-center" data-testid="emails-not-authorized">
        <Mail size={40} className="mx-auto text-amber-600 mb-3" />
        <h2 className="text-xl font-bold text-amber-900 mb-2">Zoho Desk Not Connected</h2>
        <p className="text-sm text-amber-800 mb-4">To see email tickets here, an admin needs to authorize Zoho Desk access.</p>
        <a href="/settings" className="btn-primary inline-flex items-center gap-2">Go to Settings →</a>
      </div>
    );
  }

  return (
    <div className={expanded ? "fixed inset-0 z-[60] bg-white flex" : "flex h-screen bg-white"} data-testid="emails-page">
      {/* Expanded mode close button */}
      {expanded && (
        <button onClick={() => setExpanded(false)} className="fixed top-3 right-3 z-[70] bg-white border-2 border-[--border] rounded-full p-2 hover:bg-gray-50 shadow-lg" data-testid="expand-close-btn">
          <X size={16} />
        </button>
      )}

      {/* Left: Ticket list — full-width on mobile; hidden when a ticket is opened on mobile */}
      <div className={`${selected ? "hidden md:flex" : "flex"} w-full md:w-[380px] flex-shrink-0 border-r border-[--border] bg-white flex-col`}>
          <div className="p-3 border-b border-[--border] space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-lg font-extrabold text-[--text-primary]" style={{ fontFamily: "Outfit, sans-serif" }}>Emails</h1>
                <p className="text-[11px] text-[--text-muted]">
                  {loading ? "Loading…" : `Page ${page + 1} · ${filteredTickets.length} tickets`}
                </p>
              </div>
              <button onClick={() => fetchTickets()} className="btn-secondary text-xs p-2" title="Refresh" data-testid="emails-refresh">
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              </button>
            </div>

            {/* Sort + New ticket row */}
            <div className="flex items-center gap-1.5">
              <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(0); }}
                className="flex-1 px-2 py-1 text-[11px] border border-[--border] rounded-lg outline-none bg-white" data-testid="emails-sort">
                <option value="-modifiedTime">Sort: Recent activity ↓</option>
                <option value="modifiedTime">Sort: Recent activity ↑</option>
                <option value="-createdTime">Sort: Newest first ↓</option>
                <option value="createdTime">Sort: Oldest first ↑</option>
                <option value="-priority">Sort: Priority ↓</option>
                <option value="-dueDate">Sort: Due date ↓</option>
              </select>
              <button onClick={() => setShowNewTicket(true)} className="btn-primary text-[11px] px-2 py-1 flex items-center gap-1 flex-shrink-0" title="Create new ticket in Zoho" data-testid="new-ticket-btn">
                <Mail size={11} /> New
              </button>
            </div>

            {/* Dashboard stats strip */}
            <EmailStatsStrip />

            {/* Quick view chips — primary filter */}
            <div className="flex flex-wrap gap-1">
              {QUICK_VIEWS.map((qv) => {
                const Icon = qv.icon;
                const active = quickView === qv.key;
                return (
                  <button key={qv.key} onClick={() => applyQuickView(qv.key)}
                    className={`text-[10px] font-bold px-2 py-1 rounded-full border flex items-center gap-1 transition-colors ${active ? "bg-[--accent] text-white border-[--accent]" : "bg-gray-50 text-[--text-secondary] border-[--border] hover:bg-gray-100"}`}
                    data-testid={`quickview-${qv.key}`}>
                    <Icon size={10} />{qv.label}
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <form onSubmit={handleSearch} className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[--text-muted] pointer-events-none" />
              <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search subject, email, ticket #..."
                className="w-full pl-9 pr-9 py-2 text-xs border-2 border-[--border] rounded-xl outline-none focus:border-[--accent-soft]"
                data-testid="emails-search-input" />
              {searchInput && (
                <button type="button" onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-[--text-muted] hover:text-[--accent]" data-testid="emails-search-clear">
                  <X size={14} />
                </button>
              )}
            </form>

            {/* Advanced filters — collapsible */}
            <FiltersPanel
              departmentId={departmentId} setDepartmentId={(v) => { setDepartmentId(v); setPage(0); }}
              status={status} setStatus={(v) => { setStatus(v); setPage(0); }}
              assigneeId={assigneeId} setAssigneeId={(v) => { setAssigneeId(v); setPage(0); }}
              zohoViewId={zohoViewId} setZohoViewId={(v) => { setZohoViewId(v); setPage(0); setQuickView(null); }}
              departments={zohoStatus.departments || []}
              agents={agents}
              myAgentId={myAgentId}
              zohoViews={zohoViews}
              savedViews={savedViews}
              applySavedView={applySavedView}
              onSaveClick={() => setShowSaveDialog(true)}
              onDeleteSavedView={handleDeleteView}
            />

            {/* Save view dialog */}
            {showSaveDialog && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg space-y-2" data-testid="save-view-dialog">
                <input type="text" value={viewNameInput} onChange={(e) => setViewNameInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveView()}
                  autoFocus placeholder="View name (e.g., My Open Urgent)"
                  className="w-full px-2 py-1 text-xs border border-amber-300 rounded"
                  data-testid="save-view-input" />
                <div className="flex gap-2">
                  <button onClick={handleSaveView} className="btn-primary text-[10px] flex-1" data-testid="save-view-confirm">Save</button>
                  <button onClick={() => { setShowSaveDialog(false); setViewNameInput(""); }} className="btn-secondary text-[10px]" data-testid="save-view-cancel">Cancel</button>
                </div>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && filteredTickets.length === 0 ? (
              <div className="flex items-center justify-center h-32"><Loader2 size={20} className="animate-spin text-[--text-muted]" /></div>
            ) : filteredTickets.length === 0 ? (
              <div className="p-6 text-center text-xs text-[--text-muted]">
                <Inbox size={32} className="mx-auto mb-2 text-[--text-muted]/50" />
                No tickets match your filters
              </div>
            ) : (
              filteredTickets.map((t) => (
                <div key={t.id} className={`group border-b border-[--border] hover:bg-gray-50 transition-colors ${selected?.id === t.id ? "bg-[--accent-soft]/10 border-l-2 border-l-[--accent]" : ""}`}>
                  <button onClick={() => setSelected(t)} className="w-full text-left p-3" data-testid={`email-ticket-${t.ticketNumber}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <button onClick={(e) => { e.stopPropagation(); handleStarToggle(t); }} className="flex-shrink-0" data-testid={`star-btn-${t.ticketNumber}`}>
                          <Star size={12} className={t.starred ? "fill-amber-400 text-amber-400" : "text-gray-300 hover:text-amber-400"} />
                        </button>
                        <span className="text-[10px] font-bold text-[--text-muted]">#{t.ticketNumber}</span>
                        {t.isOverdue && <span className="text-[9px] font-bold text-red-600">OVERDUE</span>}
                      </div>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${statusBadgeColor(t.statusType, t.status)} whitespace-nowrap`}>{t.status}</span>
                    </div>
                    <p className="text-xs font-semibold text-[--text-primary] line-clamp-2 mb-1" title={t.subject}>{t.subject || "(No subject)"}</p>
                    <div className="flex items-center justify-between text-[10px] text-[--text-muted]">
                      <span className="truncate max-w-[180px]" title={t.contactEmail}>
                        <UserIcon size={9} className="inline mr-0.5" />{t.contactName || t.contactEmail}
                      </span>
                      <span>{timeAgo(t.modifiedTime || t.createdTime)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[9px] text-[--text-muted] mt-0.5">
                      <span>{departmentName(t.departmentId)}</span>
                      {t.assigneeName && <span className="text-[--accent]">→ {t.assigneeName}</span>}
                    </div>
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="p-3 border-t border-[--border] flex justify-between gap-2 bg-gray-50">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0 || loading}
              className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40" data-testid="emails-prev-page">← Prev</button>
            <span className="text-[11px] text-[--text-muted] self-center">Page {page + 1}</span>
            <button onClick={() => setPage(page + 1)} disabled={!hasMore || loading}
              className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-40" data-testid="emails-next-page">Next →</button>
          </div>
        </div>

      {/* Right: Ticket detail — hidden on mobile when no ticket is selected */}
      <div className={`${selected ? "flex" : "hidden md:flex"} flex-1 bg-[--bg-cream] overflow-hidden flex-col min-w-0`} data-testid="email-detail-pane">
        {selected ? (
          <TicketDetail
            key={selected.id}
            ticket={selected}
            agents={agents}
            departmentName={departmentName(selected.departmentId)}
            onStatusChanged={fetchTickets}
            onStarToggle={handleStarToggle}
            expanded={expanded}
            onToggleExpand={() => setExpanded(v => !v)}
            onFilterByCustomer={(email) => { setSearchInput(email); setSearch(email); setPage(0); setExpanded(false); }}
            onMobileBack={() => setSelected(null)}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-center p-6">
            <div>
              <Mail size={48} className="mx-auto text-[--text-muted]/40 mb-3" />
              <p className="text-sm text-[--text-muted]">Select a ticket from the list to view the full thread</p>
            </div>
          </div>
        )}
      </div>
      {/* New Ticket Modal */}
      {showNewTicket && (
        <NewTicketModal
          onClose={() => setShowNewTicket(false)}
          onCreated={(tkt) => { setShowNewTicket(false); fetchTickets(); if (tkt) setSelected(tkt); }}
          departments={zohoStatus?.departments || []}
        />
      )}
    </div>
  );
};

/* ---- Ticket Detail — expanded view with all enhancements ---- */
const TABS = [
  { k: "threads", label: "Conversation", icon: MessageSquare },
  { k: "links", label: "Linked", icon: LinkIcon },
  { k: "lookup", label: "Order/Product", icon: Search },
  { k: "resolution", label: "Resolution", icon: CheckCircle },
  { k: "attachments", label: "Attachments", icon: Paperclip },
  { k: "time", label: "Time Entry", icon: Clock },
  { k: "history", label: "History", icon: History },
];

const TicketDetail = ({ ticket, agents, departmentName, onStatusChanged, onStarToggle, expanded, onToggleExpand, onFilterByCustomer, onMobileBack }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("threads");
  const [statusLoading, setStatusLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(ticket.status);
  const [currentAssignee, setCurrentAssignee] = useState(ticket.assigneeId || "");
  const [showContextPanel, setShowContextPanel] = useState(false);
  const [linkedLeads, setLinkedLeads] = useState([]);

  const [threads, setThreads] = useState([]);
  const [comments, setComments] = useState([]);  // Iter 143 — private notes
  const [resolution, setResolution] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [history, setHistory] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [tabLoading, setTabLoading] = useState(false);
  // Customer 360 drawer trigger — opens with the contact's phone (when known).
  const [show360Phone, setShow360Phone] = useState(null);
  // Per-thread reply target: when set, the ReplyComposer will quote this
  // thread and forward the `thread_id` to the backend so Zoho threads the
  // reply correctly.
  const [replyingToThread, setReplyingToThread] = useState(null);

  const loadLinkedLeads = useCallback(async () => {
    try {
      const r = await zohoApi.linkedLeads(ticket.id);
      setLinkedLeads(r.data.linked || []);
    } catch { /* silent */ }
  }, [ticket.id]);

  // Load detail + threads + comments
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        // Iter 143 — fire all four calls in parallel; comments was added
        // because the /threads endpoint only returns customer-facing
        // emails, and resolution is fetched eagerly so the preview banner
        // can render at the top of the conversation tab.
        const [det, thr, cmts, resR] = await Promise.allSettled([
          zohoApi.ticketDetails(ticket.id),
          zohoApi.ticketThreads(ticket.id, 30),
          zohoApi.ticketComments(ticket.id),
          zohoApi.ticketResolution(ticket.id),
        ]);
        if (cancelled) return;
        if (det.status === "fulfilled") {
          setDetail(det.value.data);
          setCurrentStatus(det.value.data.status);
          setCurrentAssignee(det.value.data.assigneeId || "");
        }
        if (thr.status === "fulfilled") setThreads(thr.value.data.threads || []);
        if (cmts.status === "fulfilled") setComments(cmts.value.data.comments || []);
        if (resR.status === "fulfilled") setResolution(resR.value.data.resolution || null);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load ticket details");
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Best-effort auto-link in background — don't await.
      try { await zohoApi.autoLinkTicket(ticket.id); } catch { /* ignore */ }
      loadLinkedLeads();
    };
    setTab("threads");
    load();
    return () => { cancelled = true; };
  }, [ticket.id, loadLinkedLeads]);

  // Iter 143 — Auto-refresh threads + comments every 30s while a ticket is
  // open. Yash: agents currently F5 the page to spot new replies. Polling
  // is throttled so we never hit Zoho's rate limit (1 call / 0.5s / scope).
  useEffect(() => {
    if (!ticket?.id) return undefined;
    const interval = setInterval(async () => {
      try {
        const [thrR, cmtR] = await Promise.allSettled([
          zohoApi.ticketThreads(ticket.id, 30),
          zohoApi.ticketComments(ticket.id),
        ]);
        if (thrR.status === "fulfilled") {
          const newThreads = thrR.value.data.threads || [];
          setThreads(prev => (newThreads.length !== prev.length ? newThreads : prev));
        }
        if (cmtR.status === "fulfilled") {
          const newComments = cmtR.value.data.comments || [];
          setComments(prev => (newComments.length !== prev.length ? newComments : prev));
        }
      } catch { /* silent — refresh failures are not user-visible */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [ticket?.id]);

  // Lazy-load secondary tabs
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (tab === "threads") return;
      setTabLoading(true);
      try {
        if (tab === "resolution") { const r = await zohoApi.ticketResolution(ticket.id); if (!cancelled) setResolution(r.data.resolution); }
        else if (tab === "attachments") { const r = await zohoApi.ticketAttachments(ticket.id); if (!cancelled) setAttachments(r.data.attachments || []); }
        else if (tab === "history") { const r = await zohoApi.ticketHistory(ticket.id); if (!cancelled) setHistory(r.data.history || []); }
        else if (tab === "time") { const r = await zohoApi.ticketTimeEntries(ticket.id); if (!cancelled) setTimeEntries(r.data.entries || []); }
      } catch { if (!cancelled) toast.error(`Failed to load ${tab}`); }
      finally { if (!cancelled) setTabLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [tab, ticket.id]);

  const handleStatusChange = async (newStatus) => {
    if (!newStatus || newStatus === currentStatus) return;
    if (!window.confirm(`Change status to "${newStatus}"?`)) return;
    setStatusLoading(true);
    try {
      await zohoApi.updateTicketStatus(ticket.id, newStatus);
      setCurrentStatus(newStatus);
      toast.success(`Status: ${newStatus}`);
      if (onStatusChanged) onStatusChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Status update failed"); }
    finally { setStatusLoading(false); }
  };

  const handleAssigneeChange = async (newId) => {
    if (newId === currentAssignee) return;
    setStatusLoading(true);
    try {
      await zohoApi.reassignTicket(ticket.id, newId);
      setCurrentAssignee(newId);
      const name = agents.find(a => a.id === newId)?.name || "agent";
      toast.success(`Transferred to ${name}`);
      if (onStatusChanged) onStatusChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Transfer failed"); }
    finally { setStatusLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 size={24} className="animate-spin text-[--text-muted]" /></div>;

  const d = detail || ticket;
  const statusType = currentStatus === "Closed" ? "Closed" : "Open";

  // SLA: compute time-since-created and first-response-time
  const firstResponseTime = threads.find(th => th.direction === "out")?.createdTime;
  const firstResponseDuration = (d.createdTime && firstResponseTime)
    ? Math.round((new Date(firstResponseTime) - new Date(d.createdTime)) / 60000)
    : null;

  // Iter 143 — match Zoho's terse header chip-bar (`#396502 · Londonsky1125
  // · ⊙ 28 Apr 10:35 PM · Product Queries`). The full absolute timestamp
  // remains as a tooltip on the chip so power-users still see seconds.
  const headerCreatedAbs = (() => {
    if (!d.createdTime) return "";
    try {
      return new Date(d.createdTime).toLocaleString("en-IN", {
        day: "2-digit", month: "short",
        hour: "2-digit", minute: "2-digit", hour12: true,
      });
    } catch { return d.createdTime; }
  })();
  const headerCreatedFull = (() => {
    if (!d.createdTime) return "";
    try {
      return new Date(d.createdTime).toLocaleString("en-IN", {
        day: "2-digit", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
      });
    } catch { return d.createdTime; }
  })();

  // Customer 360 button is meaningful only when we have a phone to lookup.
  const customer360Phone = d.contactPhone || d.phone || "";

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-[--border] px-4 md:px-6 py-3 flex-shrink-0">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-4 mb-2">
          <div className="min-w-0 flex-1 w-full">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              {/* Mobile back button — only visible <md */}
              {onMobileBack && (
                <button
                  onClick={onMobileBack}
                  className="md:hidden -ml-1 p-1 rounded-full hover:bg-gray-100 text-[--text-primary]"
                  data-testid="emails-mobile-back-btn"
                  title="Back to inbox"
                >
                  <ChevronLeft size={18} />
                </button>
              )}
              <button onClick={() => onStarToggle?.(ticket)} data-testid="detail-star-btn">
                <Star size={14} className={ticket.starred ? "fill-amber-400 text-amber-400" : "text-gray-300 hover:text-amber-400"} />
              </button>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${statusBadgeColor(statusType, currentStatus)}`}>{currentStatus}</span>
              {d.priority && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">{d.priority}</span>}
              {d.isOverdue && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">OVERDUE</span>}
              {/* Linked WhatsApp lead pill — Yash, Iter 143: prominent shortcut */}
              {linkedLeads.slice(0, 2).map((l) => (
                <a
                  key={l.id}
                  href={`/chats?lead=${l.id}`}
                  className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 inline-flex items-center gap-1"
                  title={`Open WhatsApp chat with ${l.name || l.phone}`}
                  data-testid={`linked-lead-pill-${l.id}`}
                >
                  <Phone size={10} />
                  WhatsApp · {l.name || l.phone || "lead"}
                  {l.source === "auto_order" && <Sparkles size={9} />}
                </a>
              ))}
              {linkedLeads.length > 2 && (
                <button onClick={() => setTab("links")} className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100" data-testid="linked-lead-more">
                  +{linkedLeads.length - 2} more
                </button>
              )}
            </div>
            {/* Subject — large, prominent */}
            <h2 className="text-base md:text-xl font-extrabold text-[--text-primary] mb-1.5 leading-tight break-words flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif", overflowWrap: "anywhere" }}>
              <Mail size={18} className="text-[--accent] flex-shrink-0" />
              <span>{d.subject}</span>
            </h2>
            {/* Iter 143 — Zoho-style chip row mirroring the screenshot
                 (#TicketID · Contact · ⊙ Created · Department) */}
            <div className="text-[11px] md:text-xs text-[--text-muted] flex items-center gap-1.5 md:gap-2 flex-wrap" data-testid="ticket-header-chips">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[--border] bg-gray-50 font-bold text-[--text-primary]" data-testid="chip-ticket-number">
                <IdCard size={11} />#{d.ticketNumber}
              </span>
              <button onClick={() => onFilterByCustomer?.(d.contactEmail)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[--border] bg-gray-50 hover:bg-gray-100 hover:border-[--accent] hover:text-[--accent] transition-colors max-w-[280px]" title="Show all tickets from this customer" data-testid="chip-contact">
                <UserIcon size={11} />
                <span className="truncate">{d.contactName || d.contactEmail || "(no contact)"}</span>
              </button>
              {d.createdTime && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-[--border] bg-gray-50" title={headerCreatedFull} data-testid="chip-created">
                  <Clock size={11} />{headerCreatedAbs} <span className="text-[--text-muted]/70">· {timeAgo(d.createdTime)}</span>
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-blue-200 bg-blue-50 text-blue-700 font-medium" data-testid="chip-department">
                <SlidersHorizontal size={10} />{departmentName}
              </span>
              {firstResponseDuration !== null && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 font-medium" title="Time from ticket-created to first agent reply" data-testid="chip-frt">
                  <Reply size={10} />FRT {firstResponseDuration < 60 ? `${firstResponseDuration}m` : `${Math.round(firstResponseDuration / 60)}h`}
                </span>
              )}
              {firstResponseDuration === null && currentStatus === "Open" && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-red-200 bg-red-50 text-red-700 font-medium" data-testid="chip-waiting">
                  <Clock size={10} />Waiting for reply
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap md:flex-shrink-0 md:justify-end">
            {/* Customer 360 — Iter 143: parity with ChatsPage */}
            {customer360Phone && (
              <button
                onClick={() => setShow360Phone(customer360Phone)}
                className="text-xs p-1.5 rounded-lg border-2 border-[--border] hover:border-[--accent] hover:bg-[--accent]/5 inline-flex items-center gap-1"
                title="Open Customer 360 view"
                data-testid="customer-360-btn"
              >
                <UsersIcon size={12} /><span className="hidden sm:inline">360°</span>
              </button>
            )}
            {/* Assignee transfer */}
            <select value={currentAssignee} onChange={(e) => handleAssigneeChange(e.target.value)} disabled={statusLoading}
              className="text-xs font-medium px-2 py-1.5 border-2 border-[--border] rounded-lg bg-white outline-none focus:border-[--accent] disabled:opacity-50 flex-1 min-w-[120px] md:flex-initial md:max-w-[140px]"
              data-testid="ticket-assignee-select">
              <option value="">👤 Unassigned</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            {/* Status change */}
            <select value={currentStatus} onChange={(e) => handleStatusChange(e.target.value)} disabled={statusLoading}
              className="text-xs font-bold px-2 py-1.5 border-2 border-[--border] rounded-lg bg-white outline-none focus:border-[--accent] disabled:opacity-50"
              data-testid="ticket-status-select">
              <option value="Open">Open</option>
              <option value="On Hold">On Hold</option>
              <option value="Escalated">Escalated</option>
              <option value="Closed">Closed</option>
            </select>
            <button onClick={onToggleExpand} className="btn-secondary text-xs p-1.5 hidden md:inline-flex" title={expanded ? "Collapse" : "Expand to full page"} data-testid="expand-toggle">
              {expanded ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
            </button>
            <button
              onClick={() => setShowContextPanel((v) => !v)}
              className={`text-xs p-1.5 rounded-lg border-2 transition-colors hidden md:inline-flex ${showContextPanel ? "bg-[--accent] border-[--accent] text-white" : "bg-white border-[--border] hover:border-[--accent]"}`}
              title={showContextPanel ? "Close context panel" : "Open Order/Product panel"}
              data-testid="context-panel-toggle"
            >
              {showContextPanel ? <PanelRightClose size={12} /> : <PanelRightOpen size={12} />}
            </button>
            {d.web_url && (
              <a href={d.web_url} target="_blank" rel="noreferrer" className="btn-secondary text-xs flex items-center gap-1" data-testid="open-in-zoho">
                Zoho <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>

        {/* Tabs — horizontally scrollable on mobile to handle overflow */}
        <div className="flex gap-1 border-b border-[--border] -mb-3 mt-1 overflow-x-auto no-scrollbar" style={{ scrollbarWidth: "none" }}>
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.k} onClick={() => setTab(t.k)}
                className={`px-3 py-2 text-xs font-semibold transition-colors flex items-center gap-1.5 border-b-2 flex-shrink-0 whitespace-nowrap ${tab === t.k ? "border-[--accent] text-[--accent]" : "border-transparent text-[--text-muted] hover:text-[--text-primary]"}`}
                data-testid={`tab-${t.k}`}>
                <Icon size={12} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body: Tab content + optional right context panel */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Resolution preview banner — Iter 143: collapsed at top of conversation when present */}
          {tab === "threads" && resolution && (resolution.content || resolution.resolution) && (
            <div className="mx-6 mt-3 rounded-xl border-2 border-emerald-200 bg-emerald-50/60 px-3 py-2 flex items-start gap-2" data-testid="resolution-banner">
              <CheckCircle size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-emerald-700 uppercase tracking-wide">Resolution</p>
                <p className="text-xs text-emerald-900 mt-0.5 line-clamp-2">{(resolution.content || resolution.resolution || "").replace(/<[^>]+>/g, " ").trim()}</p>
              </div>
              <button onClick={() => setTab("resolution")} className="text-[11px] font-bold text-emerald-700 hover:underline flex-shrink-0" data-testid="resolution-banner-open">View →</button>
            </div>
          )}
          {/* Auto-detect banner on threads tab */}
          {tab === "threads" && (
            <AutoDetectBanner
              subject={d.subject}
              description={d.description}
              threads={threads}
              onOpenPanel={() => setShowContextPanel(true)}
              panelOpen={showContextPanel}
            />
          )}
          {/* Tab content */}
          <div className="flex-1 overflow-y-auto px-6 py-4" data-testid={`tab-content-${tab}`}>
            {tab === "threads" && <ThreadsView threads={threads} comments={comments} onReplyToThread={(th) => {
              setReplyingToThread(th);
              // open composer + scroll to it
              setTimeout(() => {
                document.querySelector('[data-testid="reply-expand-btn"]')?.click();
                document.querySelector('[data-testid="reply-composer-textarea"]')?.scrollIntoView({ behavior: "smooth", block: "end" });
              }, 30);
            }} />}
            {tab === "links" && <LinkedLeadsView ticketId={ticket.id} contactEmail={d.contactEmail} contactPhone={d.contactPhone} onLinkChanged={loadLinkedLeads} />}
            {tab === "lookup" && <LookupView ticketId={ticket.id} subject={d.subject} description={d.description} contactPhone={d.contactPhone} />}
            {tab === "resolution" && (tabLoading ? <TabLoader /> : <ResolutionView resolution={resolution} />)}
            {tab === "attachments" && (tabLoading ? <TabLoader /> : <AttachmentsView attachments={attachments} />)}
            {tab === "history" && (tabLoading ? <TabLoader /> : <HistoryView history={history} />)}
            {tab === "time" && (tabLoading ? <TabLoader /> : <TimeEntriesView entries={timeEntries} />)}
          </div>

          {/* Reply composer — Zoho-style compact, expands on focus */}
          <ReplyComposer
            ticketId={ticket.id}
            contactEmail={d.contactEmail}
            contactName={d.contactName}
            subject={d.subject}
            threads={threads}
            replyingToThread={replyingToThread}
            onClearReplyTarget={() => setReplyingToThread(null)}
            onReplied={async () => {
              // Iter 143 — refresh BOTH threads + comments after a send so
              // private notes show up immediately too.
              try {
                const [t, c] = await Promise.all([
                  zohoApi.ticketThreads(ticket.id, 30),
                  zohoApi.ticketComments(ticket.id),
                ]);
                setThreads(t.data.threads || []);
                setComments(c.data.comments || []);
              } catch { /* silent */ }
              setReplyingToThread(null);
            }}
          />
        </div>

        {/* Right Context Panel — Iter 145: when ticket is linked to a WATI
            lead, show the FULL chats-style right rail (Info / Order /
            Product / AI tabs with labels, place-admin-order, AI suggest,
            etc.). When no link, fall back to the lighter context panel. */}
        {showContextPanel && (
          linkedLeads.length > 0 ? (
            <EmailLinkedLeadPanel
              leadId={linkedLeads[0].id}
              ticketSubject={d.subject}
              ticketStatus={currentStatus}
              onTicketStatusChange={handleStatusChange}
              statusLoading={statusLoading}
              onClose={() => setShowContextPanel(false)}
            />
          ) : (
            <ContextPanel
              subject={d.subject}
              description={d.description}
              threads={threads}
              contactPhone={d.contactPhone}
              contactName={d.contactName}
              contactEmail={d.contactEmail}
              linkedLeads={linkedLeads}
              onOpen360={() => setShow360Phone(customer360Phone)}
              onClose={() => setShowContextPanel(false)}
            />
          )
        )}
      </div>
      {/* Iter 143 — Customer 360 drawer (parity with ChatsPage) */}
      {show360Phone && (
        <Customer360Panel phone={show360Phone} onClose={() => setShow360Phone(null)} mode="drawer" />
      )}
    </div>
  );
};

/* ---- Reply Composer — multi-mode (reply / reply-all / forward / internal note) ---- */
const extractEmails = (str) => {
  if (!str) return [];
  return [...String(str).matchAll(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g)].map((m) => m[0].toLowerCase());
};

const ReplyComposer = ({ ticketId, contactEmail, contactName, subject, threads, replyingToThread, onClearReplyTarget, onReplied }) => {
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState("reply"); // reply | reply_all | forward | note
  const [content, setContent] = useState("");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subj, setSubj] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{id, name, size}]
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const isPublic = mode !== "note";

  // Auto-populate To/Cc/Subject when mode changes
  useEffect(() => {
    if (mode === "reply") {
      setTo(contactEmail || "");
      setCc("");
      setSubj("");
    } else if (mode === "reply_all") {
      setTo(contactEmail || "");
      // Pull CC list from the most recent incoming thread
      const lastIn = [...(threads || [])].reverse().find((t) => t.direction === "in");
      const candidates = [
        ...extractEmails(lastIn?.cc),
        ...extractEmails(lastIn?.to),
      ].filter((e) => e !== (contactEmail || "").toLowerCase());
      setCc([...new Set(candidates)].join(", "));
      setShowCcBcc(true);
      setSubj("");
    } else if (mode === "forward") {
      setTo("");
      setCc("");
      setSubj(subject ? (subject.toLowerCase().startsWith("fwd:") ? subject : `Fwd: ${subject}`) : "");
      // Pre-fill content with quoted thread
      if (!content) {
        const lastTh = (threads || [])[(threads || []).length - 1];
        if (lastTh) {
          const author = lastTh.author_name || lastTh.author_email || "Customer";
          setContent(
            `\n\n---------- Forwarded message ----------\nFrom: ${author}\nSubject: ${subject || ""}\n\n${stripHtml(lastTh.content || "")}`
          );
        }
      }
    } else if (mode === "note") {
      setTo(""); setCc(""); setSubj("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleFilePick = () => fileInputRef.current?.click();
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await zohoApi.uploadAttachment(ticketId, file);
        setAttachments((prev) => [...prev, { id: res.data.id, name: res.data.name, size: res.data.size }]);
      }
      toast.success(`${files.length} attachment(s) uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAttachment = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));

  const handleSend = async () => {
    const c = content.trim();
    if (!c) return;
    setLoading(true);
    try {
      const payload = {
        content: c,
        is_public: isPublic,
        mode: mode === "note" ? "reply" : mode,
        attachment_ids: attachments.map((a) => a.id),
      };
      if (replyingToThread?.id) {
        payload.thread_id = replyingToThread.id;
      }
      if (isPublic) {
        payload.to = to.trim() || contactEmail;
        if (cc.trim()) payload.cc = cc.trim();
        if (bcc.trim()) payload.bcc = bcc.trim();
        if (subj.trim()) payload.subject = subj.trim();
      }
      await zohoApi.replyToTicket(ticketId, payload);
      toast.success(
        mode === "note" ? "Note added ✓" :
        mode === "forward" ? "Forwarded ✓" :
        mode === "reply_all" ? "Reply-all sent ✓" : "Reply sent ✓"
      );
      setContent(""); setExpanded(false); setAiInstruction("");
      setAttachments([]); setCc(""); setBcc(""); setSubj(""); setShowCcBcc(false);
      setMode("reply");
      if (onReplied) await onReplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAi = async () => {
    setAiLoading(true);
    try {
      const res = await zohoApi.aiDraftReply(ticketId, { instruction: aiInstruction });
      setContent(res.data.draft || "");
      setExpanded(true);
      const n = res.data.examples_used || 0;
      if (n > 0) toast.success(`Draft ready · learned from ${n} past reply${n > 1 ? "ies" : ""}`);
      setTimeout(() => textareaRef.current?.focus(), 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI draft failed");
    } finally {
      setAiLoading(false);
    }
  };

  if (!expanded) {
    return (
      <div className="border-t border-[--border] bg-white px-4 py-2 flex items-center gap-1.5 flex-shrink-0" data-testid="reply-box-collapsed">
        <button onClick={() => { setMode("reply"); setExpanded(true); setTimeout(() => textareaRef.current?.focus(), 0); }}
          className="flex-1 text-left text-xs text-[--text-muted] px-3 py-2 border border-[--border] rounded-full hover:border-[--accent-soft] hover:bg-gray-50 transition-all"
          data-testid="reply-expand-btn">
          <Reply size={11} className="inline mr-1.5" />
          Reply to {contactEmail || "customer"}...
        </button>
        <button onClick={() => { setMode("reply_all"); setExpanded(true); }}
          className="text-[11px] font-bold text-[--text-secondary] hover:text-[--accent] border border-[--border] rounded-full px-2.5 py-1.5 hover:bg-gray-50 flex items-center gap-1 flex-shrink-0"
          data-testid="reply-all-btn-collapsed" title="Reply to all">
          <UsersIcon size={11} /> Reply All
        </button>
        <button onClick={() => { setMode("forward"); setExpanded(true); }}
          className="text-[11px] font-bold text-[--text-secondary] hover:text-[--accent] border border-[--border] rounded-full px-2.5 py-1.5 hover:bg-gray-50 flex items-center gap-1 flex-shrink-0"
          data-testid="forward-btn-collapsed" title="Forward">
          <Forward size={11} /> Forward
        </button>
        <button onClick={handleAi} disabled={aiLoading}
          className="bg-gradient-to-r from-purple-100 to-blue-100 hover:from-purple-200 hover:to-blue-200 text-purple-700 text-[11px] font-bold px-2.5 py-1.5 rounded-full flex items-center gap-1 border border-purple-200 disabled:opacity-50 flex-shrink-0"
          data-testid="ai-draft-btn-collapsed">
          {aiLoading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
          AI Draft
        </button>
      </div>
    );
  }

  const ModeBtn = ({ val, icon: Icon, label, color }) => (
    <button onClick={() => setMode(val)}
      className={`font-bold px-2.5 py-1 rounded-full transition-colors flex items-center gap-1 ${mode === val ? `bg-white ${color} shadow-sm` : "text-[--text-muted]"}`}
      data-testid={`mode-${val}`}>
      <Icon size={10} /> {label}
    </button>
  );

  return (
    <div className="border-t border-[--border] bg-white px-4 py-3 flex-shrink-0" data-testid="reply-box">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center bg-gray-100 rounded-full p-0.5 text-[10px]">
          <ModeBtn val="reply" icon={Reply} label="Reply" color="text-[--accent]" />
          <ModeBtn val="reply_all" icon={UsersIcon} label="Reply All" color="text-[--accent]" />
          <ModeBtn val="forward" icon={Forward} label="Forward" color="text-blue-600" />
          <ModeBtn val="note" icon={FileText} label="Internal Note" color="text-purple-600" />
        </div>
        <button onClick={() => { setExpanded(false); setContent(""); setAiInstruction(""); setAttachments([]); onClearReplyTarget?.(); }}
          className="text-[--text-muted] hover:text-[--text-primary]" data-testid="reply-collapse-btn">
          <X size={14} />
        </button>
      </div>

      {/* Per-thread reply banner — when the user clicked "Reply inline" on a specific thread */}
      {replyingToThread && (
        <div className="mb-2 flex items-start gap-2 rounded-xl border border-[--accent-soft]/60 bg-[--accent-soft]/10 px-3 py-2" data-testid="replying-to-banner">
          <CornerUpLeft size={12} className="text-[--accent] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 text-[11px] leading-tight">
            <div className="font-bold text-[--text-primary]">
              Replying to <span className="text-[--accent]">{replyingToThread.author_name || replyingToThread.author_email || "message"}</span>
              <span className="ml-1.5 text-[10px] font-medium text-[--text-muted]">· {timeAgo(replyingToThread.createdTime)}</span>
            </div>
            <div className="text-[10px] text-[--text-muted] truncate mt-0.5">
              {((replyingToThread.content || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 160)) || "(empty message)"}
            </div>
          </div>
          <button
            onClick={() => onClearReplyTarget?.()}
            className="text-[--text-muted] hover:text-red-500 flex-shrink-0"
            title="Clear reply target — reply at ticket level"
            data-testid="clear-reply-target-btn"
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* To/CC/BCC/Subject fields for public modes */}
      {isPublic && (
        <div className="space-y-1 mb-2 text-[11px]">
          <div className="flex items-center gap-2">
            <label className="font-bold text-[--text-muted] w-12 flex-shrink-0">To:</label>
            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com"
              className="flex-1 px-2 py-1 border border-[--border] rounded outline-none focus:border-[--accent]" data-testid="reply-to-input" />
            {!showCcBcc && (
              <button onClick={() => setShowCcBcc(true)} className="text-[10px] text-[--accent] hover:underline flex-shrink-0" data-testid="show-cc-bcc">+ Cc/Bcc</button>
            )}
          </div>
          {showCcBcc && (
            <>
              <div className="flex items-center gap-2">
                <label className="font-bold text-[--text-muted] w-12 flex-shrink-0">Cc:</label>
                <input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="comma,separated@emails.com"
                  className="flex-1 px-2 py-1 border border-[--border] rounded outline-none focus:border-[--accent]" data-testid="reply-cc-input" />
              </div>
              <div className="flex items-center gap-2">
                <label className="font-bold text-[--text-muted] w-12 flex-shrink-0">Bcc:</label>
                <input value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="hidden@recipient.com"
                  className="flex-1 px-2 py-1 border border-[--border] rounded outline-none focus:border-[--accent]" data-testid="reply-bcc-input" />
              </div>
            </>
          )}
          {(mode === "forward" || mode === "reply_all") && (
            <div className="flex items-center gap-2">
              <label className="font-bold text-[--text-muted] w-12 flex-shrink-0">Subj:</label>
              <input value={subj} onChange={(e) => setSubj(e.target.value)} placeholder="Subject"
                className="flex-1 px-2 py-1 border border-[--border] rounded outline-none focus:border-[--accent]" data-testid="reply-subject-input" />
            </div>
          )}
        </div>
      )}

      {/* AI instruction row */}
      <div className="flex gap-2 mb-2">
        <input type="text" value={aiInstruction} onChange={(e) => setAiInstruction(e.target.value)}
          placeholder="Optional: tell AI how to reply..."
          className="flex-1 px-2 py-1 text-[11px] border border-[--border] rounded-lg outline-none"
          data-testid="ai-instruction-input" />
        <button onClick={handleAi} disabled={aiLoading}
          className="bg-purple-600 text-white text-[11px] font-bold px-3 py-1 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
          data-testid="ai-draft-btn">
          {aiLoading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
          AI Draft
        </button>
      </div>

      <textarea ref={textareaRef} value={content} onChange={(e) => setContent(e.target.value)}
        placeholder={
          mode === "note" ? "Internal note (agents only)" :
          mode === "forward" ? "Add message before the forwarded content..." :
          replyingToThread ? `Reply to ${replyingToThread.author_name || replyingToThread.author_email || "this message"}...` :
          `Reply to ${to || contactEmail}...`
        } rows={4}
        className="w-full px-3 py-2 border border-[--border] rounded-lg outline-none focus:border-[--accent] text-sm resize-y leading-relaxed"
        data-testid="reply-composer-textarea"
        onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSend(); }} />

      {/* Attachments list */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2" data-testid="attachments-list">
          {attachments.map((a) => (
            <div key={a.id} className="inline-flex items-center gap-1 bg-gray-50 border border-[--border] rounded-full px-2 py-0.5 text-[10px]">
              <Paperclip size={9} />
              <span className="truncate max-w-[140px]" title={a.name}>{a.name}</span>
              <span className="text-[--text-muted]">({Math.round((a.size || 0) / 1024)}KB)</span>
              <button onClick={() => removeAttachment(a.id)} className="text-red-500 hover:text-red-700"><X size={10} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center mt-2">
        <div className="flex items-center gap-2">
          <button onClick={handleFilePick} disabled={uploading}
            className="text-[11px] text-[--text-muted] hover:text-[--accent] flex items-center gap-1 disabled:opacity-50"
            data-testid="attach-file-btn" title="Attach file">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />} Attach
          </button>
          <input ref={fileInputRef} type="file" multiple onChange={handleFileChange} className="hidden" data-testid="attach-file-input" />
          <span className="text-[10px] text-[--text-muted]">{content.length} chars · Ctrl+Enter</span>
        </div>
        <button onClick={handleSend} disabled={loading || !content.trim() || (isPublic && !to && !contactEmail)}
          className={`text-[11px] font-bold px-3 py-1.5 rounded-lg flex items-center gap-1 text-white disabled:opacity-50 ${
            mode === "note" ? "bg-purple-600 hover:bg-purple-700" :
            mode === "forward" ? "bg-blue-600 hover:bg-blue-700" :
            "bg-[--accent] hover:bg-[--accent]/90"
          }`}
          data-testid="reply-send-btn">
          {loading ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
          {loading ? "Sending…" :
            mode === "note" ? "Add Note" :
            mode === "forward" ? "Forward" :
            mode === "reply_all" ? "Send to All" : "Send"}
        </button>
      </div>
    </div>
  );
};

const TabLoader = () => <div className="flex items-center justify-center py-10"><Loader2 size={20} className="animate-spin text-[--text-muted]" /></div>;

/* ---- Threads view with auto-collapse long emails + inline reply ---- */
const ThreadsView = ({ threads, comments = [], onReplyToThread }) => {
  // Iter 143 — merge customer-facing emails (`threads`) and private
  // internal notes (`comments`) into a unified, time-sorted timeline.
  // Private items keep their distinct styling via `is_private` flag.
  const merged = useMemo(() => {
    const all = [
      ...(threads || []).map((t) => ({ ...t, _kind: "thread", is_private: false })),
      ...(comments || []).map((c) => ({ ...c, _kind: "comment", is_private: c.is_private !== false })),
    ];
    // Newest first (matches the existing thread order).
    return all.sort((a, b) => {
      const ta = a.createdTime ? new Date(a.createdTime).getTime() : 0;
      const tb = b.createdTime ? new Date(b.createdTime).getTime() : 0;
      return tb - ta;
    });
  }, [threads, comments]);

  if (merged.length === 0) return <div className="text-center text-sm text-[--text-muted] py-10">No conversation yet</div>;
  return (
    <div className="space-y-2.5 max-w-4xl mx-auto" data-testid="email-threads-list">
      {merged.map((th, i) => (
        <ThreadItem
          key={`${th._kind}-${th.id}`}
          thread={th}
          isLatest={i === 0}
          threadNumber={merged.length - i}
          totalThreads={merged.length}
          onReplyToThread={onReplyToThread}
        />
      ))}
    </div>
  );
};

// Deterministic HSL gradient per sender so conversations feel "real"
const avatarGradient = (seed) => {
  const s = String(seed || "?");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `linear-gradient(135deg, hsl(${h} 70% 52%), hsl(${(h + 40) % 360} 70% 42%))`;
};

const formatAbsoluteTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// Strip-to-plain-text for the collapsed preview line
const plainPreview = (html, n = 140) => {
  const t = (html || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
  return t.length > n ? t.slice(0, n) + "…" : t;
};

const ThreadItem = ({ thread: th, isLatest, threadNumber, totalThreads, onReplyToThread }) => {
  // Every thread is collapsible. Latest (top) is expanded by default; older ones collapse.
  const [expanded, setExpanded] = useState(isLatest);
  const isPrivateNote = th._kind === "comment" && th.is_private !== false;
  const outgoing = th.direction === "out" || isPrivateNote;  // private notes are agent-authored
  const author = th.author_name || th.author_email || "Unknown";
  const initials = (author[0] || "?").toUpperCase();
  const attachments = th.attachments || [];
  const abs = formatAbsoluteTime(th.createdTime);
  const preview = plainPreview(th.content);

  // Iter 143 — distinct visual treatment for private internal notes so
  // agents can scan a thread and instantly know which messages were
  // customer-facing vs internal-only.
  const containerCls = isPrivateNote
    ? "relative rounded-2xl border-2 border-amber-300 bg-amber-50/60 shadow-sm overflow-hidden transition-all"
    : "relative rounded-2xl border border-[--border] bg-white shadow-sm overflow-hidden transition-all hover:border-[--border-dark]";
  const headerRowCls = isPrivateNote
    ? "w-full flex items-center gap-3 px-4 py-2.5 bg-amber-100/60 hover:bg-amber-100 border-b border-amber-300/70 transition-colors cursor-pointer"
    : "w-full flex items-center gap-3 px-4 py-2.5 bg-gray-50/40 hover:bg-gray-50 border-b border-[--border]/70 transition-colors cursor-pointer";

  return (
    <div
      className={containerCls}
      data-testid={`email-thread-${th.id}`}
      data-private={isPrivateNote ? "true" : "false"}
    >
      {/* Clickable header row (always visible — toggles body) */}
      <div
        className={headerRowCls}
        data-testid={`thread-toggle-${th.id}`}
        onClick={() => setExpanded(v => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(v => !v); } }}
      >
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-extrabold text-white flex-shrink-0 shadow-sm ring-2 ring-white"
          style={{ background: outgoing ? "linear-gradient(135deg, var(--accent), #7a5d3a)" : avatarGradient(th.author_email || author) }}
          title={author}
        >
          {initials}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-[--text-primary] text-[13px] leading-tight truncate max-w-[260px]">{author}</span>
            {isPrivateNote
              ? <span className="text-[9px] font-extrabold text-amber-700 bg-amber-200/70 border border-amber-300 px-1.5 py-[1px] rounded-full uppercase tracking-wide inline-flex items-center gap-1"><Lock size={9} />Private Note</span>
              : outgoing
                ? <span className="text-[9px] font-extrabold text-[--accent] bg-[--accent-soft]/25 px-1.5 py-[1px] rounded-full uppercase tracking-wide">Agent</span>
                : <span className="text-[9px] font-extrabold text-gray-600 bg-gray-100 border border-gray-200 px-1.5 py-[1px] rounded-full uppercase tracking-wide">Customer</span>}
            {th.is_first_email && <span className="text-[9px] font-extrabold text-blue-700 bg-blue-50 px-1.5 py-[1px] rounded-full uppercase tracking-wide">Original</span>}
          </div>
          {/* When collapsed — show sender email + a body preview */}
          {expanded
            ? (th.author_email && <div className="text-[10px] text-[--text-muted] truncate">{th.author_email}</div>)
            : (
              <div className="text-[11px] text-[--text-muted] truncate">
                {preview || th.author_email || "(empty)"}
              </div>
            )
          }
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Reply-to-this-thread button — always visible in the header so agents
              don't have to expand the bubble first. Stops propagation so the
              collapse toggle doesn't fire. */}
          {!outgoing && onReplyToThread && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onReplyToThread(th); }}
              className="text-[10px] font-extrabold text-[--accent] bg-white hover:bg-[--accent-soft]/20 border border-[--accent-soft]/60 rounded-full px-2 py-0.5 flex items-center gap-1 transition-colors"
              title="Reply to this specific message"
              data-testid={`inline-reply-${th.id}`}
            >
              <Reply size={10} /> Reply
            </button>
          )}
          <span className="text-[9px] font-semibold text-[--text-muted] bg-white border border-[--border] px-1.5 py-[1px] rounded-full">
            {threadNumber}/{totalThreads}
          </span>
          <span className="text-[--text-muted] text-[11px] font-medium" title={abs}>{timeAgo(th.createdTime)}</span>
          {expanded ? <ChevronUp size={14} className="text-[--text-muted]" /> : <ChevronDown size={14} className="text-[--text-muted]" />}
        </div>
      </div>

      {/* Body (collapsible) */}
      {expanded && (
        <div className="px-4 py-3">
          <div
            className="text-sm text-[--text-primary] leading-relaxed break-words zoho-email-content"
            // scanner: SAFE — content runs through sanitizeHtml (DOMPurify strict allowlist). See /app/memory/SECURITY-NOTES.md#1
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(th.content) || "<em class='text-[--text-muted]'>(empty)</em>" }}
          />
          {abs && <div className="mt-2 text-[10px] text-[--text-muted] font-medium">{abs}</div>}

          {attachments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {attachments.map((a, i) => (
                <a
                  key={`${th.id}-att-${i}`}
                  href={a.url || a.href || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-[--text-primary] bg-white border border-[--border] rounded-full px-2 py-0.5 hover:border-[--accent] hover:text-[--accent] transition-colors"
                  data-testid={`attachment-${th.id}-${i}`}
                  title={a.name || "attachment"}
                >
                  <Paperclip size={10} /> <span className="truncate max-w-[180px]">{a.name || "attachment"}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ---- Linked WhatsApp leads tab ---- */
const LinkedLeadsView = ({ ticketId, contactEmail, contactPhone, onLinkChanged }) => {
  const [linked, setLinked] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [orderNumbers, setOrderNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        zohoApi.linkedLeads(ticketId),
        zohoApi.suggestLeads(ticketId),
      ]);
      setLinked(l.data.linked || []);
      setSuggestions(s.data.suggestions || []);
      setOrderNumbers(s.data.order_numbers_detected || []);
      if (onLinkChanged) onLinkChanged();
    } catch { }
    finally { setLoading(false); }
  }, [ticketId, onLinkChanged]);

  useEffect(() => { load(); }, [load]);

  const handleLink = async (lead, source = "manual") => {
    setLinking(true);
    try {
      await zohoApi.linkLead(ticketId, lead.id, source);
      toast.success(`Linked to ${lead.name || lead.phone}`);
      await load();
    } catch (e) { toast.error("Link failed"); }
    finally { setLinking(false); }
  };

  const handleUnlink = async (leadId) => {
    try {
      await zohoApi.unlinkLead(ticketId, leadId);
      toast.success("Unlinked");
      await load();
    } catch { toast.error("Unlink failed"); }
  };

  // Suggestions minus already-linked leads. Memoised — used twice and the
  // `linked.find` is O(n²) per render otherwise.
  const linkedIds = useMemo(() => new Set(linked.map(l => l.id)), [linked]);
  const newSuggestions = useMemo(
    () => suggestions.filter(s => !linkedIds.has(s.id)),
    [suggestions, linkedIds]
  );

  if (loading) return <TabLoader />;

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="linked-leads-view">
      {/* Detected order numbers */}
      {orderNumbers.length > 0 && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs font-bold text-blue-900 mb-1">🔗 Detected order numbers in this ticket:</p>
          <div className="flex flex-wrap gap-1">
            {orderNumbers.map(n => (
              <span key={n} className="text-[10px] font-mono bg-white border border-blue-200 px-1.5 py-0.5 rounded">{n}</span>
            ))}
          </div>
        </div>
      )}

      {/* Linked leads */}
      <div>
        <h3 className="text-sm font-bold text-[--text-primary] mb-2">🔗 Linked WhatsApp Chats ({linked.length})</h3>
        {linked.length === 0 ? (
          <p className="text-xs text-[--text-muted] italic">No WhatsApp chats linked yet. See suggestions below.</p>
        ) : (
          <div className="space-y-2">
            {linked.map(l => (
              <div key={l.id} className="flex items-center gap-3 p-3 bg-white border border-green-200 rounded-lg">
                <div className="p-1.5 bg-green-100 rounded-full"><Phone size={12} className="text-green-700" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold">{l.name || l.phone}</p>
                  <p className="text-[11px] text-[--text-muted]">+{l.phone} · status: {l.status || "—"} · {l.source === "auto_order" ? "🤖 auto" : "👤 manual"}</p>
                </div>
                <a href={`/chats?lead=${l.id}`} className="btn-secondary text-xs">Open Chat</a>
                <button onClick={() => handleUnlink(l.id)} className="text-xs text-red-600 hover:underline">Unlink</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Suggestions */}
      {newSuggestions.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-[--text-primary] mb-2">
            <Sparkles size={13} className="inline mr-1 text-amber-500" />
            Suggested WhatsApp Chats
          </h3>
          <div className="space-y-2">
            {newSuggestions.map(s => (
              <div key={s.id} className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="p-1.5 bg-amber-100 rounded-full"><Sparkles size={12} className="text-amber-700" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold">{s.name || s.phone}</p>
                  <p className="text-[11px] text-[--text-muted]">Match: <b>{s.match_reason}</b> · +{s.phone}</p>
                </div>
                <button onClick={() => handleLink(s, s.match_reason?.startsWith("order") ? "auto_order" : "manual")} disabled={linking} className="btn-primary text-xs flex items-center gap-1 disabled:opacity-50" data-testid={`link-suggestion-${s.id}`}>
                  <LinkIcon size={11} /> Link
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual link by phone */}
      <ManualLinkForm ticketId={ticketId} onLinked={load} />
    </div>
  );
};

const ManualLinkForm = ({ ticketId, onLinked }) => {
  const [phone, setPhone] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);

  const handleSearch = async () => {
    if (!phone.trim()) return;
    setSearching(true);
    try {
      const res = await leadsApi.getAll({ search: phone.trim(), limit: 5 });
      setResults(res.data.leads || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  const handleLink = async (lead) => {
    try {
      await zohoApi.linkLead(ticketId, lead.id, "manual");
      toast.success(`Linked ${lead.name}`);
      setPhone(""); setResults([]);
      if (onLinked) onLinked();
    } catch { toast.error("Link failed"); }
  };

  return (
    <div className="p-3 bg-gray-50 border border-[--border] rounded-lg">
      <h4 className="text-xs font-bold mb-2">Manually link by searching phone or name:</h4>
      <div className="flex gap-2">
        <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number or name..."
          className="flex-1 px-3 py-1.5 text-xs border border-[--border] rounded-lg" data-testid="manual-link-input" />
        <button onClick={handleSearch} disabled={searching || !phone.trim()} className="btn-secondary text-xs disabled:opacity-50" data-testid="manual-link-search">
          {searching ? "..." : "Search"}
        </button>
      </div>
      {results.length > 0 && (
        <div className="mt-2 space-y-1">
          {results.map(r => (
            <button key={r.id} onClick={() => handleLink(r)} className="w-full text-left p-2 bg-white border border-[--border] rounded hover:border-[--accent-soft] text-xs" data-testid={`manual-link-result-${r.id}`}>
              <b>{r.name}</b> · +{r.phone}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ResolutionView = ({ resolution }) => {
  if (!resolution || !resolution.resolution) return <div className="text-center text-sm text-[--text-muted] py-10">No resolution note yet</div>;
  return (
    <div className="max-w-4xl mx-auto bg-green-50 border border-green-200 rounded-xl p-4" data-testid="resolution-view">
      <div className="flex items-center gap-2 mb-2"><CheckCircle size={16} className="text-green-700" /><h3 className="font-bold text-sm text-green-900">Resolution</h3></div>
      {/* scanner: SAFE — sanitizeHtml runs DOMPurify before render. See /app/memory/SECURITY-NOTES.md#1 */}
      <div className="text-sm text-[--text-primary] leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeHtml(resolution.resolution) }} />
    </div>
  );
};

const AttachmentsView = ({ attachments }) => {
  if (attachments.length === 0) return <div className="text-center text-sm text-[--text-muted] py-10">No attachments</div>;
  return (
    <div className="space-y-2 max-w-4xl mx-auto" data-testid="attachments-view">
      {attachments.map((a) => (
        <div key={a.id} className="flex items-center gap-3 p-3 bg-white border border-[--border] rounded-lg">
          <div className="p-2 bg-gray-100 rounded"><FileText size={16} className="text-gray-600" /></div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[--text-primary] truncate">{a.name}</p>
            <p className="text-[11px] text-[--text-muted]">{a.size ? `${Math.round(a.size / 1024)} KB` : ""} · {a.createdTime?.slice(0, 10)}</p>
          </div>
          {a.href && <a href={a.href} target="_blank" rel="noreferrer" className="btn-secondary text-xs flex items-center gap-1"><Download size={11} /> Open</a>}
        </div>
      ))}
    </div>
  );
};

const HistoryView = ({ history }) => {
  if (history.length === 0) return <div className="text-center text-sm text-[--text-muted] py-10">No history events</div>;
  // Zoho ticket history returns { eventName, eventTime, actor{name}, eventInfo[{propertyName,propertyValue}] }
  return (
    <div className="space-y-2 max-w-4xl mx-auto" data-testid="history-view">
      {history.map((h, i) => {
        const info = Array.isArray(h.eventInfo) ? h.eventInfo : [];
        // Zoho's `propertyValue` is often a raw ISO timestamp for dates; skip those
        // and prefer short human-friendly properties like Description.
        const desc =
          info.find((p) => p.propertyName === "Description")?.propertyValue ||
          info.find((p) => p.propertyName === "Comment")?.propertyValue ||
          info.find((p) => p.propertyName === "StatusTo")?.propertyValue ||
          h.description || "";
        const durRaw = info.find((p) => p.propertyName === "Duration")?.propertyValue;
        const durMins = durRaw ? Math.round(Number(durRaw) / 60) : null;
        const eventName = (h.eventName || h.actionTaken || h.event || "Event").replace(/([a-z])([A-Z])/g, "$1 $2");
        const actor = h.actor?.name || h.performedBy?.name || "";
        const when = h.eventTime || h.time || h.createdTime;
        return (
          <div key={h.id || i} className="flex items-start gap-3 p-3 bg-white border border-[--border] rounded-xl" data-testid={`history-event-${i}`}>
            <div className="p-1.5 bg-[--accent-soft]/20 rounded-full mt-0.5 flex-shrink-0"><History size={12} className="text-[--accent]" /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[--text-primary]">
                <span className="font-bold">{eventName}</span>
                {actor && <span className="text-[--text-muted]"> · by {actor}</span>}
                {durMins != null && <span className="text-[--text-muted]"> · {durMins}m</span>}
              </p>
              {desc && <p className="text-xs text-[--text-muted] mt-0.5 line-clamp-2">{desc}</p>}
              <p className="text-[11px] text-[--text-muted] mt-1">{timeAgo(when)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TimeEntriesView = ({ entries }) => {
  if (entries.length === 0) return <div className="text-center text-sm text-[--text-muted] py-10">No time entries logged</div>;
  // Total time across all entries — ensures the page is actually "working".
  const totalSeconds = entries.reduce((acc, t) => acc + (Number(t.secondsSpent) || 0) + (Number(t.minutesSpent) || 0) * 60 + (Number(t.hoursSpent) || 0) * 3600, 0);
  const totH = Math.floor(totalSeconds / 3600);
  const totM = Math.floor((totalSeconds % 3600) / 60);
  return (
    <div className="space-y-2 max-w-4xl mx-auto" data-testid="time-entries-view">
      <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-[--accent-soft]/15 border border-[--accent-soft]/30" data-testid="time-entries-total">
        <span className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wide">Total Time Logged</span>
        <span className="text-sm font-extrabold text-[--accent]">{totH}h {totM}m · {entries.length} entr{entries.length === 1 ? "y" : "ies"}</span>
      </div>
      {entries.map((t) => {
        // Zoho returns secondsSpent / minutesSpent / hoursSpent (all strings). Normalize.
        const secs = (Number(t.secondsSpent) || 0) + (Number(t.minutesSpent) || 0) * 60 + (Number(t.hoursSpent) || 0) * 3600;
        const h = Math.floor(secs / 3600);
        const m = Math.floor((secs % 3600) / 60);
        const s = secs % 60;
        const label = h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
        const mode = t.mode || "MANUAL";
        const chargeType = t.requestChargeType;
        const when = t.executedTime || t.createdTime;
        return (
          <div key={t.id} className="flex items-start gap-3 p-3 bg-white border border-[--border] rounded-xl" data-testid={`time-entry-${t.id}`}>
            <div className="p-1.5 bg-[--accent-soft]/20 rounded-full mt-0.5 flex-shrink-0"><Clock size={13} className="text-[--accent]" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-[--text-primary] truncate">{t.description || "(no description)"}</span>
                {mode && <span className={`text-[9px] font-extrabold px-1.5 py-[1px] rounded-full uppercase tracking-wide ${mode === "AUTO" ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{mode}</span>}
                {chargeType && <span className="text-[9px] font-bold px-1.5 py-[1px] rounded-full bg-gray-100 text-gray-600 border border-gray-200">{chargeType}</span>}
              </div>
              <p className="text-[11px] text-[--text-muted] mt-0.5">
                {when ? new Date(when).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                {t.ownerName && <> · {t.ownerName}</>}
                {!t.ownerName && t.owner?.name && <> · {t.owner.name}</>}
              </p>
            </div>
            <span className="text-sm font-extrabold text-[--accent] whitespace-nowrap">{label}</span>
          </div>
        );
      })}
    </div>
  );
};

export default EmailsPage;

/* ---- Collapsible Filters Panel (clean, chip-first UI) ---- */
const FiltersPanel = ({
  departmentId, setDepartmentId, status, setStatus, assigneeId, setAssigneeId,
  zohoViewId, setZohoViewId, departments, agents, myAgentId, zohoViews,
  savedViews, applySavedView, onSaveClick, onDeleteSavedView,
}) => {
  const [open, setOpen] = useState(false);
  const activeCount = [departmentId, status, assigneeId, zohoViewId].filter(Boolean).length;
  const deptName = departments.find((d) => d.id === departmentId)?.name;
  const agentLabel = agents.find((a) => a.id === assigneeId)?.name;
  const viewLabel = zohoViews.find((v) => v.id === zohoViewId)?.name;
  const clearAll = () => {
    setDepartmentId(""); setStatus(""); setAssigneeId(""); setZohoViewId("");
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-[11px] font-bold text-[--text-secondary] hover:text-[--accent] transition-colors"
          data-testid="filters-toggle-btn"
        >
          <SlidersHorizontal size={12} />
          Filters
          {activeCount > 0 && (
            <span className="bg-[--accent] text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">
              {activeCount}
            </span>
          )}
          {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>
        {activeCount > 0 && (
          <button onClick={clearAll} className="text-[10px] text-red-600 hover:underline font-medium" data-testid="filters-clear-all">
            Clear all
          </button>
        )}
      </div>

      {/* Active filter chips (visible even when panel closed) */}
      {!open && activeCount > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="active-filter-chips">
          {deptName && <FilterChip label={deptName} onClear={() => setDepartmentId("")} color="blue" />}
          {status && <FilterChip label={status} onClear={() => setStatus("")} color="green" />}
          {agentLabel && <FilterChip label={agentLabel} onClear={() => setAssigneeId("")} color="purple" />}
          {viewLabel && <FilterChip label={`View: ${viewLabel}`} onClear={() => setZohoViewId("")} color="amber" />}
        </div>
      )}

      {open && (
        <div className="space-y-1.5 pt-1" data-testid="filters-expanded-panel">
          <div className="flex gap-1.5">
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}
              className="flex-1 px-2 py-1.5 text-[11px] border border-[--border] rounded-lg outline-none bg-white" data-testid="emails-dept-filter">
              <option value="">All Depts</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="flex-1 px-2 py-1.5 text-[11px] border border-[--border] rounded-lg outline-none bg-white" data-testid="emails-status-filter">
              {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}
            className="w-full px-2 py-1.5 text-[11px] border border-[--border] rounded-lg outline-none bg-white" data-testid="emails-assignee-filter">
            <option value="">All Agents</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}{a.id === myAgentId && " (Me)"}</option>)}
          </select>
          <select value={zohoViewId} onChange={(e) => setZohoViewId(e.target.value)}
            className="w-full px-2 py-1.5 text-[11px] border border-[--border] rounded-lg outline-none bg-white" data-testid="zoho-views-select">
            <option value="">Zoho Built-in Views ({zohoViews.length})</option>
            {zohoViews.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <div className="flex gap-1.5 pt-1 border-t border-[--border]/50">
            <select
              onChange={(e) => { if (e.target.value) applySavedView(JSON.parse(e.target.value)); }}
              className="flex-1 px-2 py-1.5 text-[11px] border border-[--border] rounded-lg outline-none bg-white"
              data-testid="saved-views-select"
              value=""
            >
              <option value="">Saved Views ({savedViews.length})</option>
              {savedViews.map((v) => <option key={v.id} value={JSON.stringify(v)}>{v.name}</option>)}
            </select>
            <button onClick={onSaveClick} className="btn-secondary text-[11px] px-2" title="Save current filters" data-testid="save-view-btn">
              <Save size={11} />
            </button>
          </div>
          {savedViews.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-0.5" data-testid="saved-views-chips">
              {savedViews.slice(0, 6).map((v) => (
                <div key={v.id} className="group inline-flex items-center gap-1 text-[10px] bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-2 py-0.5">
                  <button onClick={() => applySavedView(v)} className="font-medium">{v.name}</button>
                  <button onClick={() => onDeleteSavedView(v.id)} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"><X size={9} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FilterChip = ({ label, onClear, color = "gray" }) => {
  const colorMap = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    green: "bg-green-50 text-green-700 border-green-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    gray: "bg-gray-50 text-gray-700 border-gray-200",
  };
  return (
    <div className={`inline-flex items-center gap-1 text-[10px] border rounded-full px-2 py-0.5 ${colorMap[color]}`}>
      <span className="font-medium truncate max-w-[120px]">{label}</span>
      <button onClick={onClear} className="hover:opacity-70"><X size={9} /></button>
    </div>
  );
};

/* ---- Order + Product Lookup Tab (full-width view) ---- */
const LookupView = ({ ticketId, subject, description, contactPhone }) => {
  const { orders: autoOrders, items: autoItems, trackings: autoTrackings } = scanTicket(subject, description, []);

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="lookup-view">
      {/* Auto-detected summary chips */}
      {(autoOrders.length > 0 || autoItems.length > 0 || autoTrackings.length > 0) && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs font-bold text-blue-900 mb-2">🤖 Auto-detected in this ticket:</p>
          {autoOrders.length > 0 && (
            <div className="mb-2">
              <p className="text-[10px] font-bold text-blue-800 mb-1">Order numbers (≤ 6 digits):</p>
              <div className="flex flex-wrap gap-1">
                {autoOrders.map((n) => <AutoOrderChip key={n} orderNumber={n} />)}
              </div>
            </div>
          )}
          {autoTrackings.length > 0 && (
            <div className="mb-2">
              <p className="text-[10px] font-bold text-amber-800 mb-1">Tracking numbers:</p>
              <div className="flex flex-wrap gap-1">
                {autoTrackings.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 text-[10px] font-mono bg-white border border-amber-200 text-amber-700 px-2 py-0.5 rounded">
                    <Truck size={9} /> {t}
                  </span>
                ))}
              </div>
            </div>
          )}
          {autoItems.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-purple-800 mb-1">Item codes:</p>
              <div className="flex flex-wrap gap-1">
                {autoItems.map((c) => <AutoItemChip key={c} itemCode={c} />)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Auto-loaded detail cards for detected orders AND tracking numbers */}
      {(autoOrders.length > 0 || autoTrackings.length > 0) && (
        <div className="space-y-2">
          {autoOrders.map((o) => <OrderDetailCard key={`o-${o}`} orderNumber={o} />)}
          {autoTrackings.map((t) => <OrderDetailCard key={`t-${t}`} orderNumber={t} />)}
        </div>
      )}

      <OrderLookupBox />
      <ProductLookupBox subject={subject} contactPhone={contactPhone} />
    </div>
  );
};

const AutoOrderChip = ({ orderNumber }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const lookup = async () => {
    setLoading(true);
    try { const r = await orderTrackingApi.lookup(orderNumber); setData(r.data); }
    catch { setData({ error: "Not found" }); }
    finally { setLoading(false); }
  };
  return (
    <button onClick={lookup} disabled={loading}
      className="text-[10px] font-mono bg-white border border-blue-200 hover:border-blue-400 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50"
      data-testid={`auto-order-${orderNumber}`}>
      {loading ? <Loader2 size={9} className="animate-spin" /> : "#"}
      {orderNumber}
      {data && !data.error && <CheckCircle size={9} className="text-green-600" />}
    </button>
  );
};

const AutoItemChip = ({ itemCode }) => (
  <a href={`https://www.exoticindiaart.com/search/?keyword=${itemCode}`} target="_blank" rel="noreferrer"
    className="text-[10px] font-mono bg-white border border-purple-200 hover:border-purple-400 px-2 py-1 rounded inline-flex items-center gap-1"
    data-testid={`auto-item-${itemCode}`}>
    {itemCode} <ExternalLink size={8} />
  </a>
);

const OrderLookupBox = () => {
  const [orderNum, setOrderNum] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const lookup = async () => {
    if (!orderNum.trim()) return;
    setLoading(true); setData(null);
    try { const r = await orderTrackingApi.lookup(orderNum.trim()); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Order not found"); }
    finally { setLoading(false); }
  };
  const clear = () => { setOrderNum(""); setData(null); };
  return (
    <div className="rounded-xl border border-[--border] bg-gradient-to-br from-blue-50/40 via-white to-white shadow-sm overflow-hidden" data-testid="order-lookup-box">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[--border]/70 bg-white/60">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-500 text-white shadow-sm">
          <Package size={13} />
        </div>
        <div className="min-w-0">
          <h3 className="text-[12px] font-extrabold text-[--text-primary] leading-tight">Order / Tracking Lookup</h3>
          <p className="text-[9px] text-[--text-muted] leading-tight">Enter order # or courier tracking #</p>
        </div>
      </div>
      <div className="p-3">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[--text-muted] pointer-events-none" />
            <input
              type="text"
              value={orderNum}
              onChange={(e) => setOrderNum(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              placeholder="e.g. 2800273 or 21018570543"
              className="w-full pl-8 pr-8 py-2 text-xs border border-[--border] rounded-lg outline-none bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all font-mono"
              data-testid="order-lookup-input"
            />
            {orderNum && !loading && (
              <button onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 text-[--text-muted] hover:text-red-500" title="Clear" data-testid="order-lookup-clear">
                <X size={12} />
              </button>
            )}
          </div>
          <button
            onClick={lookup}
            disabled={loading || !orderNum.trim()}
            className="px-3 py-2 rounded-lg text-[11px] font-extrabold text-white bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm flex items-center gap-1 transition-all"
            data-testid="order-lookup-btn"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            <span>{loading ? "Searching" : "Search"}</span>
          </button>
        </div>
        {data?.error && (
          <p className="text-[11px] text-red-600 mt-2 bg-red-50 border border-red-200 rounded-lg px-2 py-1" data-testid="order-error">
            {data.error}
          </p>
        )}
        {data && !data.error && (data.items || []).length > 0 && (
          <div className="mt-2.5" data-testid="order-result">
            <OrderDetailCard orderNumber={data.order_number || orderNum} />
          </div>
        )}
      </div>
    </div>
  );
};

// Iter 144 — replaced the basic ProductLookupBox with this wrapper that
// reuses the rich `<ProductLookupTab>` from `/components/chat/...` so
// agents reading email tickets get the same dual-currency price cards,
// variants matrix, FX prices, leadtime, ISBN, weight, image, etc that
// chat agents already had. Self-contained — owns its own state and only
// asks the parent for the ticket subject (used as a search hint).
const ProductLookupBox = ({ subject = "", contactPhone = "" }) => {
  const [productLookup, setProductLookup] = useState("");
  const [productLoading, setProductLoading] = useState(false);
  const [productData, setProductData] = useState(null);
  const [productSuggestions, setProductSuggestions] = useState(null);

  // Best-effort priceScope from the contact's phone country. India / South
  // Asia → INR card highlighted; rest of world → USD card highlighted.
  const priceScope = useMemo(() => {
    const ph = String(contactPhone || "").replace(/\D/g, "");
    if (!ph) return "INR";
    if (ph.startsWith("91")) return "INR";  // India
    if (ph.startsWith("977") || ph.startsWith("94") || ph.startsWith("880")) return "INR"; // NP/LK/BD
    return "USD";
  }, [contactPhone]);

  const lookupProduct = useCallback(async (overrideCode) => {
    const code = (typeof overrideCode === "string" ? overrideCode : productLookup || "").trim();
    if (!code) return;
    setProductLoading(true);
    setProductData(null);
    setProductSuggestions(null);
    try {
      const r = await booksApi.lookup(code);
      setProductData(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Handle the {message, suggestions} miss-shape from the backend
      if (detail && typeof detail === "object" && Array.isArray(detail.suggestions)) {
        setProductSuggestions(detail.suggestions);
        toast.error(detail.message || "Product not found");
      } else {
        const msg = (typeof detail === "string") ? detail : (detail?.message || "Product not found");
        toast.error(msg);
        setProductSuggestions([]);
      }
    } finally {
      setProductLoading(false);
    }
  }, [productLookup]);

  // Email tickets don't have a "send WhatsApp" channel — wire `send` to
  // copy + toast the agent so they paste into the email reply.
  const buildProductDetailsMessage = useCallback((pd) => {
    if (!pd?.raw) return "";
    const det = pd.raw.details || {};
    const lines = [];
    if (det.title) lines.push(`*${det.title}*`);
    if (pd.itemcode) lines.push(`Item code: ${pd.itemcode}`);
    if (det.author) lines.push(`Author: ${det.author}`);
    if (det.publisher) lines.push(`Publisher: ${det.publisher}`);
    if (det.pages) lines.push(`Pages: ${det.pages}`);
    if (det.language) lines.push(`Language: ${det.language}`);
    if (det.binding) lines.push(`Binding: ${det.binding}`);
    if (det.isbn) lines.push(`ISBN: ${det.isbn}`);
    return lines.join("\n");
  }, []);

  const copyProductSummary = useCallback(() => {
    if (!productData?.raw) return;
    const msg = buildProductDetailsMessage(productData);
    navigator.clipboard.writeText(msg);
    toast.success("Product summary copied — paste into reply");
  }, [productData, buildProductDetailsMessage]);

  // From the email surface, "send" doesn't make sense (no WATI lead) —
  // copy and instruct the agent to paste into the reply composer.
  const sendProductSummary = copyProductSummary;

  return (
    <div className="rounded-xl border border-[--border] bg-white overflow-hidden" data-testid="rich-product-lookup">
      <div className="px-3 py-2 border-b border-[--border] bg-gradient-to-r from-violet-50 to-purple-50">
        <p className="text-xs font-bold text-violet-900 inline-flex items-center gap-1.5">
          <BookOpen size={12} /> Product Lookup
          <span className="text-[10px] text-violet-600 font-normal">· dual-currency, variants, ISBN</span>
        </p>
      </div>
      <ProductLookupTab
        priceScope={priceScope}
        detectedProducts={[]}
        productLookup={productLookup}
        setProductLookup={setProductLookup}
        productLoading={productLoading}
        productData={productData}
        setProductData={setProductData}
        productSuggestions={productSuggestions}
        setProductSuggestions={setProductSuggestions}
        lookupProduct={lookupProduct}
        selectedLead={null}
        buildProductDetailsMessage={buildProductDetailsMessage}
        copyProductSummary={copyProductSummary}
        sendProductSummary={sendProductSummary}
      />
    </div>
  );
};

/* ---- Shared helpers: scan subject + description + threads for order#s, item codes, tracking#s ---- */
const stripHtml = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
const extractFromText = (text) => {
  const clean = stripHtml(text || "");
  // strip URLs and emails to reduce false positives on item-code regex
  const neutral = clean.replace(/https?:\/\/\S+/gi, " ").replace(/\S+@\S+\.\S+/g, " ");
  // Rule: Orders are EXO-prefixed OR bare 4-6 digit numeric. Anything >6 digits is NOT an order.
  const orders = [
    ...[...clean.matchAll(/EXO[-\s]?(\d{4,7})/gi)].map((m) => m[1]),
    ...[...clean.matchAll(/\border[\s:#]*(\d{4,6})\b/gi)].map((m) => m[1]),
    ...[...neutral.matchAll(/(?:^|\s)#?(\d{4,6})(?:\s|$|,|\.)/g)].map((m) => m[1]),
  ];
  // Tracking numbers: 7+ digit numeric (e.g., "21018570543") or alphanumeric courier patterns
  const trackings = [
    ...[...neutral.matchAll(/\b(\d{9,})\b/g)].map((m) => m[1]),
    // Common courier prefix patterns: DTDC/Bluedart/Fedex/DHL style
    ...[...neutral.matchAll(/\b([A-Z]{2,4}\d{7,})\b/g)].map((m) => m[1]),
  ];
  const items = [...neutral.matchAll(/\b([a-z]{2,4}\d{3,5})\b/gi)].map((m) => m[1].toLowerCase());
  return {
    orders: [...new Set(orders)],
    trackings: [...new Set(trackings)],
    items: [...new Set(items)],
  };
};
const scanTicket = (subject, description, threads) => {
  const all = [subject || "", description || "", ...(threads || []).map((th) => th.content || "")].join(" \n ");
  return extractFromText(all);
};

/* ---- Auto-Detect Banner (compact top bar on threads tab) ---- */
const AutoDetectBanner = ({ subject, description, threads, onOpenPanel, panelOpen }) => {
  const { orders, items, trackings } = scanTicket(subject, description, threads);
  if (orders.length === 0 && items.length === 0 && trackings.length === 0) return null;
  return (
    <div
      className="flex items-center gap-2 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-b border-blue-200/60 px-4 py-1.5 flex-shrink-0 overflow-x-auto"
      data-testid="auto-detect-banner"
    >
      <Sparkles size={12} className="text-purple-600 flex-shrink-0" />
      <span className="text-[10px] font-bold text-purple-800 flex-shrink-0">Auto-detected:</span>
      {orders.slice(0, 4).map((o) => (
        <span key={`o-${o}`} className="inline-flex items-center gap-1 text-[10px] font-mono bg-white/80 border border-blue-200 text-blue-700 px-2 py-0.5 rounded-full flex-shrink-0" data-testid={`banner-order-${o}`}>
          <Package size={9} /> #{o}
        </span>
      ))}
      {trackings.slice(0, 3).map((t) => (
        <span key={`t-${t}`} className="inline-flex items-center gap-1 text-[10px] font-mono bg-white/80 border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full flex-shrink-0" title="Tracking number" data-testid={`banner-tracking-${t}`}>
          <Truck size={9} /> {t.length > 14 ? `${t.slice(0, 14)}…` : t}
        </span>
      ))}
      {items.slice(0, 4).map((c) => (
        <span key={`i-${c}`} className="inline-flex items-center gap-1 text-[10px] font-mono bg-white/80 border border-purple-200 text-purple-700 px-2 py-0.5 rounded-full flex-shrink-0" data-testid={`banner-item-${c}`}>
          <BookOpen size={9} /> {c}
        </span>
      ))}
      {(orders.length > 4 || items.length > 4 || trackings.length > 3) && (
        <span className="text-[10px] text-[--text-muted] flex-shrink-0">+{Math.max(0, orders.length + items.length + trackings.length - 11)} more</span>
      )}
      <button
        onClick={onOpenPanel}
        className="ml-auto text-[10px] font-bold text-purple-700 hover:text-purple-900 flex items-center gap-1 flex-shrink-0"
        data-testid="banner-open-panel"
      >
        {panelOpen ? "Panel open" : "View details"}
        <PanelRightOpen size={10} />
      </button>
    </div>
  );
};

/* ---- Right Context Panel (closeable) ----
 * Layout:
 *   - Sticky HEADER with close button
 *   - Sticky LOOKUP section (Order # + Item # search boxes) — always visible
 *     at the top regardless of how many auto-detected orders we have below.
 *   - Scrollable body with Detected Orders / Tracking #s / Products.
 *   - Each detected-order card has a × delete button (local state) so the
 *     agent can hide numbers that aren't actual order IDs.
 */
const ContextPanel = ({ subject, description, threads, contactPhone, contactName, contactEmail, linkedLeads = [], onOpen360, onClose }) => {
  const { orders, items, trackings } = scanTicket(subject, description, threads);
  // Local UI state — agents can dismiss false-positive detected orders.
  // Re-keyed off the ticket subject so switching tickets resets the hidden set.
  const hiddenKey = `ctx-hidden:${subject || ""}`;
  const [hidden, setHidden] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem(hiddenKey) || "[]")); }
    catch { return new Set(); }
  });
  const hide = (k) => {
    const next = new Set(hidden); next.add(k); setHidden(next);
    try { sessionStorage.setItem(hiddenKey, JSON.stringify([...next])); } catch {}
  };
  const visOrders = orders.filter((o) => !hidden.has(`o:${o}`));
  const visTrackings = trackings.filter((t) => !hidden.has(`t:${t}`));
  const visItems = items.filter((i) => !hidden.has(`i:${i}`));

  return (
    <aside
      className="w-[340px] flex-shrink-0 border-l border-[--border] bg-white flex flex-col"
      data-testid="context-panel"
    >
      {/* Header — sticky */}
      <div className="flex-shrink-0 bg-white border-b border-[--border] px-3 py-2 flex items-center justify-between">
        <h3 className="text-xs font-extrabold text-[--text-primary] flex items-center gap-1.5">
          <Sparkles size={12} className="text-purple-600" /> Order & Product Context
        </h3>
        <button onClick={onClose} className="text-[--text-muted] hover:text-[--text-primary]" title="Close panel" data-testid="context-panel-close">
          <X size={14} />
        </button>
      </div>

      {/* Iter 144 — Customer info card. Mirrors the "Info" tab from the
          ChatsPage right-rail so an email agent has the same one-glance
          customer summary as a chat agent. Hidden when we don't have a
          contact attached to the ticket (system tickets, unassigned). */}
      {(contactName || contactEmail || contactPhone) && (
        <div className="flex-shrink-0 px-3 py-2.5 border-b border-[--border] bg-gradient-to-br from-[--bg-cream] to-white" data-testid="context-customer-card">
          <p className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wide mb-1.5">Customer</p>
          <div className="space-y-1">
            {contactName && (
              <div className="flex items-center gap-1.5 text-xs text-[--text-primary]">
                <UserIcon size={11} className="text-[--text-muted] flex-shrink-0" />
                <span className="font-bold truncate">{contactName}</span>
              </div>
            )}
            {contactEmail && (
              <div className="flex items-center gap-1.5 text-[11px] text-[--text-muted]">
                <Mail size={10} className="flex-shrink-0" />
                <a href={`mailto:${contactEmail}`} className="truncate hover:text-[--accent]">{contactEmail}</a>
              </div>
            )}
            {contactPhone && (
              <div className="flex items-center gap-1.5 text-[11px] text-[--text-muted]">
                <Phone size={10} className="flex-shrink-0" />
                <span className="truncate font-mono">{contactPhone}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {contactPhone && (
              <button onClick={onOpen360} className="text-[10px] font-bold px-2 py-1 rounded-md border border-[--accent] text-[--accent] hover:bg-[--accent] hover:text-white transition-colors inline-flex items-center gap-1" data-testid="ctx-360-btn">
                <UsersIcon size={10} /> 360° View
              </button>
            )}
            {linkedLeads[0] && (
              <a href={`/chats?lead=${linkedLeads[0].id}`} className="text-[10px] font-bold px-2 py-1 rounded-md border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 inline-flex items-center gap-1" data-testid="ctx-open-chat-btn">
                <MessageSquare size={10} /> Open WhatsApp
              </a>
            )}
          </div>
        </div>
      )}

      {/* Lookup section — ALWAYS pinned at the top so the agent can search
          regardless of how many cards are listed below. */}
      <div className="flex-shrink-0 bg-white border-b border-[--border] px-3 py-3 space-y-3" data-testid="context-lookup-pinned">
        <OrderLookupBox />
        <ProductLookupBox subject={subject} contactPhone={contactPhone} />
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {visOrders.length === 0 && visItems.length === 0 && visTrackings.length === 0 && (
          <p className="text-[11px] text-[--text-muted] italic" data-testid="context-empty">
            Nothing auto-detected. Use the search boxes above.
          </p>
        )}

        {visOrders.length > 0 && (
          <section data-testid="context-orders-section">
            <h4 className="text-[10px] font-bold text-blue-800 uppercase tracking-wide mb-1.5">
              Detected Orders ({visOrders.length})
            </h4>
            <div className="space-y-2">
              {visOrders.map((o) => (
                <OrderDetailCard key={o} orderNumber={o} onDismiss={() => hide(`o:${o}`)} />
              ))}
            </div>
          </section>
        )}

        {visTrackings.length > 0 && (
          <section data-testid="context-trackings-section">
            <h4 className="text-[10px] font-bold text-amber-800 uppercase tracking-wide mb-1.5">
              Detected Tracking #s ({visTrackings.length})
            </h4>
            <div className="space-y-2">
              {visTrackings.map((t) => (
                <OrderDetailCard key={`trk-${t}`} orderNumber={t} onDismiss={() => hide(`t:${t}`)} />
              ))}
            </div>
          </section>
        )}

        {visItems.length > 0 && (
          <section data-testid="context-items-section">
            <h4 className="text-[10px] font-bold text-purple-800 uppercase tracking-wide mb-1.5">
              Detected Products ({visItems.length})
            </h4>
            <div className="space-y-2">
              {visItems.map((c) => (
                <ProductDetailCard key={c} itemCode={c} onDismiss={() => hide(`i:${c}`)} />
              ))}
            </div>
          </section>
        )}
      </div>
    </aside>
  );
};

/* ---- Order Detail Card (auto-loads order info — shows all items + courier/tracking/country) ---- */
const OrderDetailCard = ({ orderNumber, onDismiss }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await orderTrackingApi.lookup(orderNumber);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderNumber]);

  const DismissBtn = onDismiss ? (
    <button
      onClick={onDismiss}
      title="Not an order number — hide this card"
      className="ml-2 text-[--text-muted] hover:text-red-600 transition-colors flex-shrink-0"
      data-testid={`order-card-dismiss-${orderNumber}`}
    >
      <X size={11} />
    </button>
  ) : null;

  if (loading) {
    return (
      <div className="p-2 bg-blue-50/40 border border-blue-200 rounded-lg text-[11px] flex items-center gap-2" data-testid={`order-card-${orderNumber}`}>
        <Loader2 size={10} className="animate-spin text-blue-500" />
        <span className="font-mono font-bold text-blue-900 flex-1">#{orderNumber}</span>
        {DismissBtn}
      </div>
    );
  }
  if (error || data?.error) {
    return (
      <div className="p-2 bg-red-50/40 border border-red-200 rounded-lg text-[11px]" data-testid={`order-card-${orderNumber}`}>
        <div className="flex items-center justify-between">
          <div className="font-mono font-bold text-red-900">#{orderNumber}</div>
          {DismissBtn}
        </div>
        <p className="text-red-600 text-[10px]">{error || data?.error}</p>
      </div>
    );
  }
  const items = data?.items || [];
  return (
    <div className="bg-blue-50/40 border border-blue-200 rounded-lg text-[11px] overflow-hidden" data-testid={`order-card-${orderNumber}`}>
      <div className="flex items-center justify-between px-2 py-1 bg-blue-100/60 border-b border-blue-200">
        <span className="font-mono font-bold text-blue-900">#{data?.order_number || orderNumber}</span>
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-blue-700">{data?.order_date}</span>
          {DismissBtn}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-[10px] text-red-600 p-2">No items</p>
      ) : (
        <div className="divide-y divide-blue-100">
          {items.map((it, i) => <OrderItemRow key={`${orderNumber}-${i}`} item={it} />)}
        </div>
      )}
    </div>
  );
};

const OrderItemRow = ({ item }) => {
  const statusColor = {
    Shipped: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Pending: "bg-amber-100 text-amber-800 border-amber-200",
    Cancelled: "bg-red-100 text-red-800 border-red-200",
  }[item.status] || "bg-gray-100 text-gray-800 border-gray-200";
  const shippedDate = item.shipped_date ? String(item.shipped_date).slice(0, 10) : null;
  const categoryIcon = { book: "📚", painting: "🎨", statue: "🗿", jewelry: "💎" }[item.category?.toLowerCase()] || "📦";
  const trackLink = item.tracking_number ? getCourierTrackingLink(item.courier, item.tracking_number) : null;
  return (
    <div className="p-2 space-y-0.5" data-testid={`order-item-${item.item_code}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-blue-900 truncate flex-1" title={item.product_title}>
          <span className="mr-1">{categoryIcon}</span>{item.product_title || item.item_code}
        </span>
        <span className={`text-[9px] font-bold border rounded-full px-1.5 py-0.5 flex-shrink-0 ${statusColor}`}>{item.status || "—"}</span>
      </div>
      {/* Line 1: identifiers + price */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-blue-900/80">
        {item.item_code && <span><b>Code:</b> <span className="font-mono">{item.item_code}</span></span>}
        {item.category && <span><b>Cat:</b> {item.category}</span>}
        {item.price && <span><b>₹{item.price}</b></span>}
        {item.size && <span><b>Size:</b> {item.size}</span>}
        {item.color && <span><b>Color:</b> {item.color}</span>}
      </div>
      {/* Line 2: timing */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-blue-900/80">
        {item.leadtime_days && <span><b>Leadtime:</b> {item.leadtime_days}d</span>}
        {item.expected_ship_date && <span><b>Expected:</b> {item.expected_ship_date}</span>}
        {item.days_since_order != null && <span className="text-[--text-muted]">{item.days_since_order}d since order</span>}
        {item.is_overdue && <span className="text-red-600 font-bold">⚠ OVERDUE</span>}
      </div>
      {/* Line 3: courier + tracking */}
      {(item.tracking_number || item.courier) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-blue-900/80 pt-1 mt-1 border-t border-blue-100">
          {item.courier && <span className="inline-flex items-center gap-1"><Truck size={10} /> <b>{item.courier}</b></span>}
          {item.tracking_number && (
            <span className="inline-flex items-center gap-1">
              <b>Track:</b>
              <span className="font-mono select-all bg-white border border-blue-200 px-1.5 py-[1px] rounded">
                {item.tracking_number}
              </span>
            </span>
          )}
          {shippedDate && <span><b>Shipped:</b> {shippedDate}</span>}
          {trackLink && (
            <a
              href={trackLink.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[10px] font-extrabold text-white bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 px-2 py-0.5 rounded-full shadow-sm transition-all"
              data-testid={`track-courier-btn-${item.item_code}`}
              title={`Open tracking on ${trackLink.label}`}
            >
              Track on {trackLink.label}
              <ExternalLink size={9} />
            </a>
          )}
        </div>
      )}
      {/* Line 4: countries */}
      {(item.Shipping_Country || item.Billing_Country) && (
        <div className="flex gap-x-3 text-[10px] text-[--text-muted]">
          {item.Shipping_Country && <span>🚚 Ship to: <b>{item.Shipping_Country}</b></span>}
          {item.Billing_Country && item.Billing_Country !== item.Shipping_Country && <span>💳 Bill: <b>{item.Billing_Country}</b></span>}
        </div>
      )}
    </div>
  );
};

/* ---- Product Detail Card (auto-loads item info) ---- */
const ProductDetailCard = ({ itemCode, onDismiss }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await booksApi.lookup(itemCode);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Not found");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [itemCode]);
  return (
    <div className="p-2 bg-purple-50/40 border border-purple-200 rounded-lg text-[11px]" data-testid={`product-card-${itemCode}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono font-bold text-purple-900">{itemCode}</span>
        <div className="flex items-center gap-2">
          {loading && <Loader2 size={10} className="animate-spin text-purple-500" />}
          {onDismiss && (
            <button
              onClick={onDismiss}
              title="Not a product — hide this card"
              className="text-[--text-muted] hover:text-red-600 transition-colors"
              data-testid={`product-card-dismiss-${itemCode}`}
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>
      {error && <p className="text-red-600 text-[10px]">{error}</p>}
      {data && data.raw && (() => {
        const det = data.raw.details || {};
        const pri = data.pricing_resolved || {};
        const log = data.logistics_resolved || {};
        const title = det.title || det.name;
        return (
          <div className="space-y-0.5 text-[10px] text-purple-900/90">
            {title && <div className="font-semibold line-clamp-2">{title}</div>}
            {data.category && <div><b>Category:</b> {data.category}</div>}
            {det.color && <div><b>Color:</b> {det.color}</div>}
            {det.size && <div><b>Size:</b> {det.size}</div>}
            {pri.selling_inr != null && (
              <div><b>INR:</b> ₹{pri.selling_inr}{pri.discount_india > 0 ? ` (−${pri.discount_india}%)` : ""}</div>
            )}
            {pri.selling_usd != null && <div><b>USD:</b> ${pri.selling_usd}</div>}
            {log.weight_display && <div><b>Weight:</b> {log.weight_display}</div>}
            {log.local_stock != null && (
              <div><b>Stock:</b> {log.local_stock > 0 ? `${log.local_stock} in stock` : "Out of stock"}</div>
            )}
            {log.leadtime_days != null && <div><b>Leadtime:</b> {log.leadtime_days} days</div>}
          </div>
        );
      })()}
    </div>
  );
};

/* ---- Email Stats Strip (compact dashboard on sidebar header) ---- */
const EmailStatsStrip = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await zohoApi.stats();
        if (!cancelled) setStats(r.data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    const id = setInterval(load, 180000); // refresh every 3 min
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  if (loading) {
    return <div className="h-8 bg-gray-50 border border-[--border] rounded-lg animate-pulse" data-testid="stats-strip-loading" />;
  }
  if (!stats) return null;
  const frt = stats.avg_first_response_minutes;
  const frtLabel = frt == null ? "—" : frt < 60 ? `${frt}m` : `${Math.round(frt / 60)}h`;
  return (
    <div className="grid grid-cols-4 gap-1 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 border border-amber-200/60 rounded-lg p-1.5" data-testid="stats-strip">
      <Stat label="Open" value={stats.open} color="text-emerald-700" testid="stat-open" />
      <Stat label="Overdue" value={stats.overdue} color="text-red-600" testid="stat-overdue" />
      <Stat label="Avg 1st-Reply" value={frtLabel} color="text-blue-700" testid="stat-frt" />
      <Stat label="Resolved %" value={`${stats.resolution_rate_pct}%`} color="text-purple-700" testid="stat-resolution" />
    </div>
  );
};

const Stat = ({ label, value, color, testid }) => (
  <div className="text-center leading-tight" data-testid={testid}>
    <div className={`text-[13px] font-extrabold ${color}`}>{value}</div>
    <div className="text-[8px] font-bold text-[--text-muted] uppercase tracking-wide">{label}</div>
  </div>
);

/* ---- Modal: create a new Zoho ticket from CRM ---- */
const NewTicketModal = ({ onClose, onCreated, departments }) => {
  const [form, setForm] = useState({
    contact_name: "", contact_email: "", contact_phone: "",
    subject: "", description: "",
    department_id: departments?.[0]?.id || "",
    priority: "Medium",
  });
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.subject.trim() || !form.description.trim() || (!form.contact_email && !form.contact_phone)) {
      toast.error("Subject, description, and email OR phone are required");
      return;
    }
    setLoading(true);
    try {
      const res = await zohoApi.createTicket(form);
      toast.success(`Ticket #${res.data.ticket?.ticketNumber} created`);
      onCreated?.(res.data.ticket);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="new-ticket-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-[--border] px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-[--text-primary] flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Mail size={16} className="text-[--accent]" /> Create Zoho Ticket
          </h2>
          <button type="button" onClick={onClose} className="text-[--text-muted] hover:text-[--text-primary]" data-testid="new-ticket-close"><X size={16} /></button>
        </header>

        <div className="p-5 space-y-3">
          <Field label="Customer name">
            <input value={form.contact_name} onChange={(e) => upd("contact_name", e.target.value)} placeholder="Jane Customer"
              className="w-full px-3 py-2 text-sm border border-[--border] rounded-lg outline-none focus:border-[--accent]" data-testid="nt-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email *">
              <input type="email" value={form.contact_email} onChange={(e) => upd("contact_email", e.target.value)} placeholder="jane@example.com"
                className="w-full px-3 py-2 text-sm border border-[--border] rounded-lg outline-none focus:border-[--accent]" data-testid="nt-email" />
            </Field>
            <Field label="Phone">
              <input value={form.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} placeholder="+91..."
                className="w-full px-3 py-2 text-sm border border-[--border] rounded-lg outline-none focus:border-[--accent]" data-testid="nt-phone" />
            </Field>
          </div>
          <Field label="Subject *">
            <input value={form.subject} onChange={(e) => upd("subject", e.target.value)} placeholder="Order #12345 delivery issue" required
              className="w-full px-3 py-2 text-sm border border-[--border] rounded-lg outline-none focus:border-[--accent]" data-testid="nt-subject" />
          </Field>
          <Field label="Description *">
            <textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={5} placeholder="Full details of the issue..." required
              className="w-full px-3 py-2 text-sm border border-[--border] rounded-lg outline-none focus:border-[--accent] resize-y" data-testid="nt-description" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Department">
              <select value={form.department_id} onChange={(e) => upd("department_id", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[--border] rounded-lg outline-none bg-white" data-testid="nt-dept">
                {(departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select value={form.priority} onChange={(e) => upd("priority", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[--border] rounded-lg outline-none bg-white" data-testid="nt-priority">
                <option>Low</option><option>Medium</option><option>High</option>
              </select>
            </Field>
          </div>
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-[--border] px-5 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary text-xs" disabled={loading}>Cancel</button>
          <button type="submit" disabled={loading} className="btn-primary text-xs flex items-center gap-1" data-testid="nt-submit">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {loading ? "Creating…" : "Create Ticket"}
          </button>
        </footer>
      </form>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wide mb-1 block">{label}</span>
    {children}
  </label>
);
